import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { DateTime } from 'luxon';
import { UsersService } from '../../@services/users.service';
import { ToastService } from '../../@services/toast.service';
import { agGridThemes } from '../../@shared/ag-grid-themes';

/**
 * NYSE Exhibit B compliance report (IT-F2-223).
 *
 * 36-column pipe-delimited submission format the exchange requires for
 * nonprofessional data-user registration. Live-compiled from
 * F2-ADMIN.Exchange_Agreements on request; backend Redis-caches the
 * result and invalidates the cache when any agreement is written.
 *
 * Two exports:
 *   - Copy to Excel: TSV (tab-delimited) to clipboard. Excel paste
 *     auto-formats into rows/columns.
 *   - Export CSV: pipe-delimited (|) download per §4.2.5 spec, filename
 *     shape exhibit-b_<customer>_YYYY-MM-DD.csv.
 *
 * Admin-role only per IT-F2-223 Q7=B (2026-09-01).
 */

// Non-exchange display columns. Excluded from CSV export (which is the
// strict 36-col NYSE Exhibit B submission format) but included in the
// grid + Copy-to-Excel TSV. Mike asks 2026-09-01.
const DISPLAY_ONLY_COLS: { key: string; header: string }[] = [
  { key: 'CUSTOMER', header: 'Customer' },
  { key: 'SCANNERS', header: 'Scanners' },
  { key: 'PRO_STATUS', header: 'Pro / Non-Pro' },
  // EARLIEST_DATE = MIN(created_at) across the main+legacy dedupe merge.
  // Exposes "when did this user first sign?" even when the winning doc
  // is the newer main-side re-signed version.
  { key: 'EARLIEST_DATE', header: 'First Signed' },
  // Latest UserTracking/Legacy_UserTracking end_date across all matching
  // rows; blank when user is currently active on any tracking record.
  { key: 'DEACTIVATION_DATE', header: 'Deactivated' },
  // Real email — surfaced as a distinct "Emails" column since USER_ID
  // now holds the F2 UUID per Mike 2026-09-01 c/c3af6599. Backing field
  // is the NYSE-spec SUBSCRIBERS_EMAIL_ADDRESS.
  { key: 'SUBSCRIBERS_EMAIL_ADDRESS', header: 'Emails' },
  // Most-recent login from AuthLogins main+legacy — troubleshooting.
  // Mike ask 2026-09-01 c/3fb76608.
  { key: 'LAST_LOGIN', header: 'Last Login' },
];

// Ordered column keys — MUST match the backend get_exhibit_b_report row
// shape order exactly. Iteration order drives CSV column order too.
const EXHIBIT_B_COLS: { key: string; header: string }[] = [
  { key: 'USER_ID', header: 'USER_ID' },
  { key: 'SUBSCRIBERS_LAST_NAME', header: 'SUBSCRIBERS_LAST_NAME' },
  { key: 'SUBSCRIBERS_FIRST_NAME', header: 'SUBSCRIBERS_FIRST_NAME' },
  { key: 'SUBSCRIBERS_MIDDLE_NAME', header: 'SUBSCRIBERS_MIDDLE_NAME' },
  { key: 'SUBSCRIBERS_SUFFIX_NAME', header: 'SUBSCRIBERS_SUFFIX_NAME' },
  { key: 'SUBSCRIBERS_ADDRESS_ST_1', header: 'SUBSCRIBERS_ADDRESS_ST_1' },
  { key: 'SUBSCRIBERS_ADDRESS_ST_2', header: 'SUBSCRIBERS_ADDRESS_ST_2' },
  { key: 'SUBSCRIBERS_CITY', header: 'SUBSCRIBERS_CITY' },
  { key: 'SUBSCRIBERS_STATE', header: 'SUBSCRIBERS_STATE' },
  { key: 'SUBSCRIBERS_ZIP_CODE', header: 'SUBSCRIBERS_ZIP_CODE' },
  { key: 'SUBSCRIBERS_COUNTRY', header: 'SUBSCRIBERS_COUNTRY' },
  { key: 'EMPLOYERS_NAME', header: 'EMPLOYERS_NAME' },
  { key: 'EMPLOYERS_ADDRESS', header: 'EMPLOYERS_ADDRESS' },
  { key: 'EMPLOYERS_CITY', header: 'EMPLOYERS_CITY' },
  { key: 'EMPLOYERS_STATE', header: 'EMPLOYERS_STATE' },
  { key: 'EMPLOYERS_ZIP_CODE', header: 'EMPLOYERS_ZIP_CODE' },
  { key: 'EMPLOYERS_COUNTRY', header: 'EMPLOYERS_COUNTRY' },
  { key: 'SUBSCRIBERS_OCCUPATION', header: 'SUBSCRIBERS_OCCUPATION' },
  { key: 'SUBSCRIBERS_TITLE/POSITION', header: 'SUBSCRIBERS_TITLE/POSITION' },
  { key: 'SUBSCRIBERS_EMPLOYMENT_FUNCTION', header: 'SUBSCRIBERS_EMPLOYMENT_FUNCTION' },
  { key: 'MPID_TRADER_NAME_OR_ID_USED', header: 'MPID_TRADER_NAME_OR_ID_USED' },
  { key: 'SUBSCRIBERS_EMAIL_ADDRESS', header: 'SUBSCRIBERS_EMAIL_ADDRESS' },
  { key: 'SUBSCRIBERS_TELEPHONE_NUMBER', header: 'SUBSCRIBERS_TELEPHONE_NUMBER' },
  { key: 'ACCOUNT_TYPE', header: 'ACCOUNT_TYPE' },
  { key: 'Q1', header: 'Q1' },
  { key: 'Q2', header: 'Q2' },
  { key: 'Q3', header: 'Q3' },
  { key: 'Q4', header: 'Q4' },
  { key: 'Q5', header: 'Q5' },
  { key: 'Q6', header: 'Q6' },
  { key: 'Q7', header: 'Q7' },
  { key: 'Q8', header: 'Q8' },
  { key: 'Q9', header: 'Q9' },
  { key: 'Q10', header: 'Q10' },
  { key: 'Q11', header: 'Q11' },
  { key: 'DATE_AND_TIME_STAMP', header: 'DATE_AND_TIME_STAMP' },
];

// IT-F2-367 Jim 2026-09-11 13:55Z — NYSE market-data feed catalog for
// the Company Info checkbox picker. Drawn from NYSE Comprehensive Market
// Data Policies PDF + CTA/UTP tape designators. Operator ticks the
// feeds F2 is entitled to; each tick pushes a row into nyse_cta_codes
// with label+tier+network baked in — no numeric code lookup required.
// The pipe-delimited export uses c.code if manually set, else falls
// back to c.label (so the human-readable feed name lands in
// TYPE_OF_DATA / PRODUCT columns).
export interface NyseCtaCatalogItem {
  label: string;
  network: 'Network A' | 'Network B' | 'Options' | 'Bonds';
  tier: 'pro' | 'nonpro';
  // Numeric NYSE Product Code (Professional User Fee row) from
  // NYSE_Market_Data_Pricing.pdf May 14 2026 — the code that lands in
  // the pipe-delimited §9.2 export's TYPE_OF_DATA column. Blank when
  // the PDF has no numeric code for the feed (e.g. CTA plan feeds,
  // NYSE Chicago fka Texas non-launched, NYSE Bonds).
  code?: string;
}
export const NYSE_CTA_CATALOG: NyseCtaCatalogItem[] = [
  // Network A — NYSE-listed securities (codes from NYSE Pricing PDF §1)
  // CTA Network A Pro code from CTA Plan Market Data Invoice Sample
  // (ctaplan.com/publicdocs/.../Market_Data_Invoice_Sample.pdf) — 35000
  // MDS DEVICES = CTA Network A Professional Service Product Code.
  { label: 'CTA Network A',                      network: 'Network A', tier: 'pro', code: '35000' },
  { label: 'NYSE Integrated Feed',               network: 'Network A', tier: 'pro', code: '38008' },
  { label: 'NYSE OpenBook Ultra',                network: 'Network A', tier: 'pro', code: '32991' },
  { label: 'NYSE OpenBook Aggregated',           network: 'Network A', tier: 'pro', code: '32999' },
  { label: 'NYSE BBO',                           network: 'Network A', tier: 'pro', code: '32928' },
  { label: 'NYSE Trades',                        network: 'Network A', tier: 'pro', code: '32400' },
  { label: 'NYSE Order Imbalances',              network: 'Network A', tier: 'pro', code: '32641' },
  { label: 'NYSE BQT',                           network: 'Network A', tier: 'pro', code: '32452' },
  { label: 'NYSE Pillar Depth',                  network: 'Network A', tier: 'pro', code: '39603' },
  // NYSE Chicago (was NYSE Texas in older docs) — Pricing PDF §4 currently
  // shows n/a for the Pro Device fee (no numeric code assigned yet).
  { label: 'NYSE Chicago Integrated Feed',       network: 'Network A', tier: 'pro' },
  { label: 'NYSE Chicago BBO',                   network: 'Network A', tier: 'pro' },
  { label: 'NYSE Chicago Trades',                network: 'Network A', tier: 'pro' },
  // Network B — NYSE Arca / NYSE American / NYSE National / regional-listed (Pricing PDF §2, §3, §5)
  // CTA Network B Pro code from CTA Plan Market Data Invoice Sample —
  // 41004 CTA NETWORK B DVCS = Professional Service Product Code.
  { label: 'CTA Network B',                      network: 'Network B', tier: 'pro', code: '41004' },
  { label: 'NYSE Arca Integrated Feed',          network: 'Network B', tier: 'pro', code: '39068' },
  { label: 'NYSE ArcaBook',                      network: 'Network B', tier: 'pro' },
  { label: 'NYSE Arca BBO',                      network: 'Network B', tier: 'pro', code: '39017' },
  { label: 'NYSE Arca Trades',                   network: 'Network B', tier: 'pro', code: '32402' },
  { label: 'NYSE Arca Order Imbalances',         network: 'Network B', tier: 'pro' },
  { label: 'NYSE American Integrated Feed',      network: 'Network B', tier: 'pro', code: '38017' },
  { label: 'NYSE American OpenBook Ultra',       network: 'Network B', tier: 'pro', code: '32699' },
  { label: 'NYSE American OpenBook Aggregated',  network: 'Network B', tier: 'pro', code: '32599' },
  { label: 'NYSE American BBO',                  network: 'Network B', tier: 'pro', code: '32931' },
  { label: 'NYSE American Trades',               network: 'Network B', tier: 'pro', code: '32403' },
  { label: 'NYSE American Order Imbalances',     network: 'Network B', tier: 'pro', code: '32643' },
  { label: 'NYSE National Integrated Feed',      network: 'Network B', tier: 'pro', code: '37256' },
  { label: 'NYSE National BBO',                  network: 'Network B', tier: 'pro' },
  { label: 'NYSE National Trades',               network: 'Network B', tier: 'pro' },
  // Options — Pricing PDF §6 (single code per exchange top/deep bundle; complex is separate)
  { label: 'NYSE American Options',              network: 'Options',   tier: 'pro', code: '39102' },
  { label: 'NYSE American Options Complex',      network: 'Options',   tier: 'pro', code: '37058' },
  { label: 'NYSE Arca Options',                  network: 'Options',   tier: 'pro', code: '39102' },
  { label: 'NYSE Arca Options Complex',          network: 'Options',   tier: 'pro', code: '37058' },
  // Bonds — Pricing PDF has no Pro Device numeric code for Bonds feeds yet
  { label: 'NYSE Bonds Depth of Book',           network: 'Bonds',     tier: 'pro' },
  { label: 'NYSE Bonds BBO',                     network: 'Bonds',     tier: 'pro' },
  { label: 'NYSE Bonds Trades',                  network: 'Bonds',     tier: 'pro' },
];
// Retained for the datalist fallback on the Custom Entries table below
// the checkbox picker (operator may still type a bespoke label).
export const NYSE_CTA_DATA_TYPE_SUGGESTIONS: string[] = NYSE_CTA_CATALOG.map(c => c.label);

// Jim 2026-09-11 15:15Z cid c6fa4fd1 — PRODUCT column enum per NYSE
// §9.2 Professional Users Entitlement Record spec. Prior default was
// "Device"; picker now offers the full canonical set. Bake as the
// PRODUCT value on both checkbox tick and Custom Entries row edit.
export const NYSE_PRODUCT_TYPE_OPTIONS: string[] = [
  'Device',
  'Data Feed',
  'API',
  'Custom Application',
  'Non-Display Service',
];

@Component({
  selector: 'app-compliance-report',
  imports: [AgGridAngular, CommonModule, FormsModule, ReactiveFormsModule, MatSelectModule],
  templateUrl: './compliance-report.component.html',
  styleUrl: './compliance-report.component.scss'
})
export class ComplianceReportComponent {
  nyse_data_type_suggestions = NYSE_CTA_DATA_TYPE_SUGGESTIONS;
  nyse_product_type_options = NYSE_PRODUCT_TYPE_OPTIONS;
  gridDarkTheme = agGridThemes.dark;
  theme: any;
  grid: any;
  rows: any[] = [];

  customers: any[] = [
    { id: 'alpha-shark', name: 'Alpha Shark' },
    { id: 'delphi-sig', name: 'Delphi Signal' },
    { id: 'mta', name: 'MTA' },
    { id: 'option-pit', name: 'Option Pit' },
    { id: 'simpler-trading', name: 'Simpler Trading' },
    { id: 'strat-alerts', name: 'Strat Alerts' },
    { id: 'theo-trade', name: 'Theo Trade' },
    { id: 'mil-pub', name: 'Millionaire Publishing' },
  ];

  filter_customers = new FormControl<string[]>([]);
  filter_date_from = new FormControl<string>('');
  filter_date_to = new FormControl<string>('');
  // Month-range picker (YYYY-MM strings). Either or both can be blank
  // (blank = open-ended on that end). When both are set to the same
  // month, it's effectively a single-month filter. Expands under the
  // hood to date_from = first-of-<from>, date_to = end-of-<to>.
  // Mike asks 2026-09-01 c/df740ec8 (single month) + c/8a904317 (range).
  filter_month_from = new FormControl<string>('');
  filter_month_to = new FormControl<string>('');
  month_options: { id: string; label: string }[] = [];
  // IT-F2-426 (Mike 2026-09-24 checklist 3b879378): "give me a check box
  // that locks the to month to the from month for easier one month
  // select and when unchecked will allow different to month select, which
  // should only show valid months based on the from month so you don't
  // get your range inverted".
  lock_to_month = false;
  // Field the date range applies to. Backend switches semantics based on
  // this. Options mirror the display-only date columns + activation:
  //   login_periods (default): AuthLogins-derived usage periods (Mike
  //     c/d526cc06 2026-09-29 "make the login usage period the default
  //     date field filter because that seems to be what we use the most")
  //   active_in_window: scanner subscription overlapped window
  //   first_signed:  EARLIEST_DATE inside window
  //   activated:     activation date (min tracking.start_date) inside window
  //   deactivated:   DEACTIVATION_DATE inside window (excludes currently active)
  filter_date_field = new FormControl<string>('login_periods');
  date_field_options = [
    { id: 'active_in_window', label: 'Active during window' },
    { id: 'first_signed', label: 'First Signed date' },
    { id: 'activated', label: 'Activation date' },
    { id: 'deactivated', label: 'Deactivation date' },
    // Emits one row per user per usage-period, computed from AuthLogins
    // (main + legacy). A period = consecutive months with ≥1 login;
    // a gap of 1+ month starts a new period. Deactivation blank on the
    // current (incomplete) month. Mike ask 2026-09-01 c/13ce8d69.
    { id: 'login_periods', label: 'Login usage periods (multi-row/user)' },
  ];
  show_help = false;

  // View mode toggle: 'users' shows the standard one-row-per-user grid;
  // 'monthly_counts' shows first-signed user counts grouped by month.
  // Mike ask 2026-09-01. Uses current filter state for the source rows,
  // so filter picks (customer / date range / date field) narrow the
  // counts too.
  view_mode: 'users' | 'monthly_counts' | 'login_periods' | 'employees' | 'source_coverage' | 'report_page' = 'users';
  // Live-updated count of selected users grid rows so the "Clear
  // Selection (N)" button label reflects reality without a manual poll.
  // Mike ask 2026-09-01 c/a0b9b549.
  selected_count = 0;
  monthly_counts_rows: { MONTH: string; PRO: number; NON_PRO: number; PRO_DELAYED?: number; DELAYED?: number; TOTAL: number; CUMULATIVE: number }[] = [];
  // IT-F2-426 b86d9456 (Mike 2026-09-25 c/0999f711): per-month Delayed
  // counts (users seen in a login source with no Exchange_Agreement,
  // scoped to the current customers + date-range filter). Populated by
  // _load_delayed_monthly_counts alongside load_report, merged into
  // monthly_counts_rows by _rebuild_monthly_counts.
  private _delayed_by_month: Record<string, number> = {};
  // IT-F2-426 2e8a3714 (Mike 2026-09-28 c/9f324d58): Counts by Month
  // needs a STABLE user universe across date-window changes. this.rows
  // is scoped to filter_date_from/to (correct for Users view — the
  // operator wants to see only Feb-active users when they filter Feb).
  // Counts by Month needs the full population so Feb's count is the
  // same whether the operator picks Feb-only or Feb-Mar. Fetched with
  // an unbounded date range from the same endpoint.
  private monthly_source_rows: any[] = [];
  // IT-F2-426 (Mike 2026-09-24 c/b89b32fe): per-database per-month unique
  // user counts backing the new "Source Coverage" view. Fetched on-demand
  // from /admin/authlogins-source-monthly. PRO_DELAYED added
  // c/cb897b64.
  // IT-F2-426 c/6d63f68d: DELAYED is a distinct 4th class (users seen in
  // a login source with NO Exchange_Agreement on file) — not folded into
  // Pro-Delayed. All three source-monthly rows carry both PRO_DELAYED and
  // DELAYED counts.
  source_coverage_rows: { SOURCE: string; MONTH: string; PRO: number; NON_PRO: number; PRO_DELAYED?: number; DELAYED?: number; TOTAL: number }[] = [];
  // IT-F2-426 checklist c0019445 (Mike 2026-09-24 c/6c21ee1e): new "Report"
  // tab — event-count table + unique-user table shown side-by-side with
  // click-a-cell drill-down popup. Two arrays, same (SOURCE, MONTH) key
  // shape as source_coverage_rows.
  source_events_rows: { SOURCE: string; MONTH: string; PRO: number; NON_PRO: number; PRO_DELAYED?: number; DELAYED?: number; TOTAL: number }[] = [];
  source_uniques_rows: { SOURCE: string; MONTH: string; PRO: number; NON_PRO: number; PRO_DELAYED?: number; DELAYED?: number; TOTAL: number }[] = [];
  report_page_loading = false;
  // Ordered list of sources so the two side-by-side tables share the same
  // row order (matches per-source aggregation ordering).
  report_page_sources: string[] = ['AuthLogins (main)', 'AuthLogins (legacy)', 'Graphem_Compliance', 'AuthLogins_Graphem'];
  // Click-a-cell drill-down modal state.
  drill_open = false;
  drill_loading = false;
  drill_title = '';
  drill_source = '';
  drill_month = '';
  drill_kind: 'events' | 'uniques' = 'uniques';
  drill_bucket: 'PRO' | 'NON_PRO' | 'PRO_DELAYED' | 'DELAYED' | 'TOTAL' = 'TOTAL';
  drill_rows: { EMAIL: string; CUSTOMER: string; PRO_STATUS: string; EVENT_COUNT: number; FIRST_EVENT?: string; LAST_EVENT?: string; AGREEMENT_DATE?: string; FIRST_SIGNED?: string; STILL_ACTIVE?: boolean; USER_ID?: string; SOURCE?: string }[] = [];
  drill_totals: { PRO: number; NON_PRO: number; PRO_DELAYED: number; DELAYED: number; EVENTS: number; UNIQUES: number } = { PRO: 0, NON_PRO: 0, PRO_DELAYED: 0, DELAYED: 0, EVENTS: 0, UNIQUES: 0 };
  drill_source_kind: 'authlogins' | 'graphem' = 'authlogins';
  // IT-F2-426 518a835e: same modal serves the Counts by Month drill-down.
  // Toggle switches which calc-explanation blurb + Copy-to-Excel header
  // shape gets used.
  drill_context: 'source_monthly' | 'counts_by_month' = 'source_monthly';
  // IT-F2-426 18d4b626 (Mike 2026-09-28 c/b65cd473): drill modal renders
  // as ag-grid with column filters + sort. gridApi captured on ready so
  // the bucket filter chips can call setRowData / setFilterModel.
  drill_grid_api: any = null;
  drill_grid_column_defs: any[] = [];
  // IT-F2-426 18d4b626: nested "View Logins" modal.
  logins_open = false;
  logins_loading = false;
  logins_email = '';
  logins_customer = '';
  logins_month = '';        // IT-F2-426 d0e59c93: drill month scope carried through
  logins_rows: any[] = [];
  logins_totals: { rows: number; main: number; legacy: number; graphem?: number; al_graphem?: number } = { rows: 0, main: 0, legacy: 0, graphem: 0, al_graphem: 0 };
  logins_grid_api: any = null;
  logins_grid_column_defs: any[] = [];
  // IT-F2-426 b6683cc5 (Mike 2026-09-25): opt-in to include the whole
  // visible view in the Debug Copy payload so operators can share the
  // full story (rendered rows for the active tab) with one paste.
  // IT-F2-426 543c819f (Mike 2026-09-25 c/7d5d938b): persist across
  // reloads so the operator's preference sticks. Seeded from localStorage
  // in ngOnInit; writes back on every change via _persist_debug_include.
  debug_copy_include_view_data = false;
  private _DEBUG_INCLUDE_LS_KEY = 'compliance_report_debug_copy_include_view_data';
  _persist_debug_include(v: boolean) {
    this.debug_copy_include_view_data = v;
    try { localStorage.setItem(this._DEBUG_INCLUDE_LS_KEY, v ? '1' : '0'); } catch { /* private mode */ }
  }
  // Captured at ngOnInit before any URL mutation. on_grid_ready reads
  // this — NOT route.snapshot — because snapshot is a live getter that
  // reflects the current URL, and our first _sync_url_from_filters() call
  // (during initial load_report) will have wiped `gf` before the grid
  // could apply it. Bug caught 2026-09-01 IT-F2-223.
  private _initial_gf_from_url: string | null = null;

  // IT-F2-367 — firm compliance metadata cached from Customer.f2 via
  // GET /admin/company-info. Fed into the Employee (§9.2) entitlement
  // report so operators don't get prompted per run. Editable via the
  // "Edit Company Info" button which opens the modal below.
  company_info: any = {};
  show_company_info_modal = false;
  editing_company_info: any = {};

  // IT-F2-367 — Employee view mode: list, add, edit, delete F2 employee
  // agreement rows for the compliance report. Backed by
  // GET/POST/DELETE /rest/user/data-agreements/admin/employee(s).
  employee_rows: any[] = [];
  show_employee_modal = false;
  editing_employee: any = {};
  is_new_employee = false;

  // IT-F2-391 c/a4c363b1 (2026-09-15) — Review workflow spun out to its
  // own admin panel at /admin/exchange-agreement-review. All pending_review*
  // state and handlers moved to ExchangeAgreementReviewComponent.

  constructor(
    private adminService: UsersService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.init_grid();
    this.theme = this.gridDarkTheme;
  }

  async ngOnInit() {
    // IT-F2-426 543c819f: rehydrate the Debug-Copy include-view-data
    // preference so it persists across reloads.
    try {
      const raw = localStorage.getItem(this._DEBUG_INCLUDE_LS_KEY);
      if (raw === '1') this.debug_copy_include_view_data = true;
    } catch { /* private mode */ }

    // IT-F2-426 18d4b626: seed the drill-modal + login-history grid column
    // defs (they don't depend on view state, just static shape).
    this.drill_grid_column_defs = this._build_drill_grid_column_defs();
    this.logins_grid_column_defs = this._build_logins_grid_column_defs();

    // Seed filters from URL query params so refresh + shared URLs
    // reproduce the same view (Mike 2026-09-01). `customers` is a
    // comma-separated slug list; `date_from`/`date_to` are ISO YYYY-MM-DD;
    // `date_field` picks which date the range applies to.
    const qp = this.route.snapshot.queryParamMap;
    const cust_raw = qp.get('customers') || '';
    if (cust_raw) this.filter_customers.setValue(cust_raw.split(',').filter(Boolean));
    const df = qp.get('date_from') || '';
    const dt = qp.get('date_to') || '';
    if (df) this.filter_date_from.setValue(df);
    if (dt) this.filter_date_to.setValue(dt);
    const df_field = qp.get('date_field') || '';
    if (df_field && this.date_field_options.some(o => o.id === df_field)) {
      this.filter_date_field.setValue(df_field);
    }
    // IT-F2-426 c845fcec (Mike 2026-09-25 c/7d335dbf): view_mode picker
    // persists to ?tab= so refresh keeps the tab. Only accept known values;
    // silently ignore garbage.
    const tab_raw = qp.get('tab') || '';
    const valid_tabs = ['users', 'monthly_counts', 'login_periods', 'employees', 'source_coverage', 'report_page'] as const;
    if ((valid_tabs as readonly string[]).includes(tab_raw)) {
      this.view_mode = tab_raw as any;
    }
    // Capture gf now — the initial load_report() below calls
    // _sync_url_from_filters() before grid is ready, which wipes the
    // param from the URL. on_grid_ready reads _initial_gf_from_url
    // instead of the URL snapshot.
    this._initial_gf_from_url = qp.get('gf');
    // Seed month options — last 5 years back through current+3-month forward,
    // most recent first. Mike ask 2026-09-01 c/df740ec8: only month/year
    // ever needed, no specific dates.
    this._seed_month_options();
    // If URL had date_from/date_to set (e.g. from an older link), infer months.
    if (this.filter_date_from.value) {
      const d = DateTime.fromISO(this.filter_date_from.value);
      if (d.isValid) this.filter_month_from.setValue(d.toFormat('yyyy-MM'));
    }
    if (this.filter_date_to.value) {
      const d = DateTime.fromISO(this.filter_date_to.value);
      if (d.isValid) this.filter_month_to.setValue(d.toFormat('yyyy-MM'));
    }
    // Auto-re-fire the report on any top-bar filter change so the operator
    // doesn't have to click Refresh. Month picker expands to date_from +
    // date_to which triggers load_report through their subscriptions.
    this.filter_date_field.valueChanges.subscribe(() => {
      // IT-F2-426 e265b763: monthly view column labels + tooltips change
      // per date-field pick (First-Signed / Activation / Deactivation /
      // Active during window). Rebuild the columnDefs on the grid AFTER
      // load_report re-fetches so the header text tracks the new field.
      this.load_report().then(() => {
        if (this.view_mode === 'monthly_counts' && this.grid?.api) {
          this.grid.api.setGridOption('columnDefs', this._current_column_defs());
        }
      });
    });
    this.filter_date_from.valueChanges.subscribe(() => {
      this.load_report();
      // IT-F2-426 ace10742: Report tab honors the Date From/To filter.
      if (this.view_mode === 'report_page') this._load_report_page();
    });
    this.filter_date_to.valueChanges.subscribe(() => {
      this.load_report();
      if (this.view_mode === 'report_page') this._load_report_page();
    });
    this.filter_customers.valueChanges.subscribe(() => {
      this.load_report();
      // IT-F2-426 c/b89b32fe: also refresh Source Coverage if the operator
      // is currently on that tab (or has it warmed up in this session).
      if (this.view_mode === 'source_coverage') this._load_source_coverage();
      // IT-F2-426 c0019445: refresh Report page too if it's the active tab.
      if (this.view_mode === 'report_page') this._load_report_page();
    });
    // IT-F2-426 checklist 3b879378: if lock-to-from is on, mirror From
    // into To before load_report reads them. Otherwise, if the user
    // manually picked a To that's now before the new From, clamp To
    // silently to prevent an inverted range at load time.
    this.filter_month_from.valueChanges.subscribe(() => {
      if (this.lock_to_month) {
        const fromYm = this.filter_month_from.value || '';
        if (this.filter_month_to.value !== fromYm) {
          this.filter_month_to.setValue(fromYm, { emitEvent: false });
        }
      } else {
        const fromYm = this.filter_month_from.value || '';
        const toYm = this.filter_month_to.value || '';
        if (fromYm && toYm && toYm < fromYm) {
          this.filter_month_to.setValue(fromYm, { emitEvent: false });
        }
      }
      this._apply_month_range();
    });
    this.filter_month_to.valueChanges.subscribe(() => this._apply_month_range());
    // IT-F2-367 — load firm compliance metadata for the Employee report
    // (VAN, VAN_NAME, legal name, installation address, TYPE_OF_PRODUCT).
    // Fire-and-forget; the export buttons will re-fetch if this hasn't
    // resolved by click time.
    this._load_company_info();
    await this.load_report();
    // IT-F2-426 c845fcec: if the URL seeded a non-default tab, kick off its
    // load-on-entry (source_coverage / report_page / employees). load_report
    // populates `rows` for Users / Counts by Month / Login Periods; the
    // other tabs need their own dedicated fetch.
    if (this.view_mode === 'source_coverage') this._load_source_coverage();
    else if (this.view_mode === 'report_page') this._load_report_page();
    else if (this.view_mode === 'employees') this._load_employees();
  }

  private async _load_company_info() {
    const r: any = await this.adminService.getComplianceCompanyInfo();
    if (r && !r.err) this.company_info = r;
  }

  // IT-F2-367 — Edit Company Info modal for firm-level compliance metadata
  // (VAN, VAN_NAME, legal name, installation address, TYPE_OF_PRODUCT).
  // Persists to Customer.f2 via POST /admin/company-info. Fetched at
  // component init + on export click; edited via the "Edit Company Info"
  // button that opens the modal below.
  openCompanyInfoModal() {
    this.editing_company_info = {
      legal_name: this.company_info.legal_name || '',
      address: this.company_info.address || '',
      city: this.company_info.city || '',
      state: this.company_info.state || '',
      zip_code: this.company_info.zip_code || '',
      country: this.company_info.country || 'US',
      nyse_vendor_account_number: this.company_info.nyse_vendor_account_number || '',
      data_center_location: {
        name: this.company_info.data_center_location?.name || '',
        address: this.company_info.data_center_location?.address || '',
        city: this.company_info.data_center_location?.city || '',
        state: this.company_info.data_center_location?.state || '',
        zip_code: this.company_info.data_center_location?.zip_code || '',
      },
      default_type_of_product: this.company_info.default_type_of_product || 'Device',
      // IT-F2-367 — NYSE CTA product codes editable list. Each entry:
      // {code, label, tier:'pro'|'nonpro'}. Employee Entitlement export
      // iterates the pro-tier codes; Non-Pro Entitlement iterates nonpro.
      // Codes are firm-specific (assigned per NYSE registration) — not
      // enumerated in the Comprehensive Policy PDF.
      nyse_cta_codes: JSON.parse(JSON.stringify(this.company_info.nyse_cta_codes || [])),
      // IT-F2-426 c/d1856280: Pro-tier realtime data reporting settings.
      // When pro_15min_delayed=true, non-employee Pros are excluded from
      // the compliance reports (they're not receiving live data → not
      // reportable to NYSE). Optional cutoff date includes them again
      // from that date forward.
      pro_15min_delayed: this.company_info.pro_15min_delayed === true,
      pro_live_data_start_date: this.company_info.pro_live_data_start_date || '',
    };
    // Jim 2026-09-11 14:48Z Q8=C: on modal open, backfill CODE from the
    // NYSE Pricing PDF catalog when the row's label matches a known
    // feed. Preserves any manually-set numeric code. Falls back to
    // label only when catalog has no numeric code (CTA plan feeds,
    // Bonds, Chicago — Pricing PDF has n/a for those Pro Device rows).
    // Jim 2026-09-11 15:15Z cid c6fa4fd1: also fix PRODUCT column when
    // an earlier PR left product = label (which lands in the §9.2
    // PRODUCT export column as e.g. "CTA Network A"); replace with the
    // company default_type_of_product (spec enum value like "Device").
    const default_product = this.editing_company_info.default_type_of_product || 'Device';
    const catalog_labels = new Set(NYSE_CTA_CATALOG.map(c => c.label.toLowerCase()));
    for (const r of (this.editing_company_info.nyse_cta_codes || [])) {
      if (!r.code) {
        const match = NYSE_CTA_CATALOG.find(
          c => c.label.toLowerCase() === (r.label || '').trim().toLowerCase()
        );
        r.code = (match && match.code) || r.label || '';
      }
      // Repair rows where product was accidentally set to the feed
      // label by a prior version of toggleCtaCatalogItem.
      if (r.product && catalog_labels.has(String(r.product).toLowerCase())) {
        r.product = default_product;
      }
      if (!r.product) r.product = default_product;
    }
    this.show_company_info_modal = true;
  }

  addCtaCode() {
    if (!this.editing_company_info.nyse_cta_codes) this.editing_company_info.nyse_cta_codes = [];
    const default_product = this.editing_company_info.default_type_of_product || 'Device';
    this.editing_company_info.nyse_cta_codes.push({ code: '', label: '', tier: 'pro', product: default_product });
  }

  removeCtaCode(i: number) {
    (this.editing_company_info.nyse_cta_codes || []).splice(i, 1);
  }

  // IT-F2-367 Jim 2026-09-11 13:55Z — checkbox picker for NYSE feeds.
  // Each catalog item toggles a row in nyse_cta_codes; label match is the
  // key so we don't require operators to know numeric CTA product codes.
  // Custom entries (numeric code + custom label) still live in the table
  // below the picker and coexist in the same nyse_cta_codes array.
  nyse_cta_catalog = NYSE_CTA_CATALOG;

  get ctaCatalogGrouped(): { network: string; items: NyseCtaCatalogItem[] }[] {
    const groups: Record<string, NyseCtaCatalogItem[]> = {};
    for (const item of NYSE_CTA_CATALOG) {
      (groups[item.network] ||= []).push(item);
    }
    return Object.keys(groups).map(network => ({ network, items: groups[network] }));
  }

  isCtaCatalogItemChecked(item: NyseCtaCatalogItem): boolean {
    const arr = this.editing_company_info?.nyse_cta_codes || [];
    return arr.some((c: any) => (c.label || '').trim().toLowerCase() === item.label.toLowerCase());
  }

  toggleCtaCatalogItem(item: NyseCtaCatalogItem) {
    if (!this.editing_company_info.nyse_cta_codes) this.editing_company_info.nyse_cta_codes = [];
    const arr: any[] = this.editing_company_info.nyse_cta_codes;
    const idx = arr.findIndex(c => (c.label || '').trim().toLowerCase() === item.label.toLowerCase());
    if (idx >= 0) {
      arr.splice(idx, 1);
    } else {
      // Jim 2026-09-11 14:48Z Q8=C: bake the NYSE Product Code from the
      // Pricing PDF Pro Device row into CODE. Falls back to label when
      // PDF has no numeric code for the feed (CTA plan feeds, Bonds,
      // Chicago). Operator can still override manually.
      // Jim 2026-09-11 15:15Z cid c6fa4fd1: PRODUCT defaults to the
      // company's configured default_type_of_product (spec value like
      // "Device"), NOT the feed label — PRODUCT column in the §9.2
      // export must be one of the NYSE spec enum values.
      const default_product = this.editing_company_info?.default_type_of_product || 'Device';
      arr.push({ code: item.code || item.label, label: item.label, tier: item.tier, product: default_product, network: item.network });
    }
  }

  closeCompanyInfoModal() {
    this.show_company_info_modal = false;
  }

  async saveCompanyInfo() {
    // IT-F2-426 c/d1856280: detect Pro-tier-setting changes before save so
    // we know whether to re-fire the report after the persisted shape
    // lands. Server-side cache is auto-invalidated by patch_compliance_
    // company_info when either pro_15min_delayed / pro_live_data_start_date
    // is present in the body.
    const prevPro = this.company_info?.pro_15min_delayed === true;
    const prevCut = String(this.company_info?.pro_live_data_start_date || '');
    const nextPro = this.editing_company_info?.pro_15min_delayed === true;
    const nextCut = String(this.editing_company_info?.pro_live_data_start_date || '');
    const proSettingsChanged = prevPro !== nextPro || prevCut !== nextCut;

    const r: any = await this.adminService.patchComplianceCompanyInfo(this.editing_company_info);
    if (r?.err) {
      this.toast.error('Save failed', r.err);
      return;
    }
    this.toast.success('Saved', 'Company info updated on Customer.f2');
    // Re-fetch to pick up the persisted shape (with any server defaults).
    await this._load_company_info();
    this.closeCompanyInfoModal();
    if (proSettingsChanged) {
      // Report set changed under the new filter — re-fire the compilation
      // and the source-coverage aggregation.
      await this.load_report();
      if (this.view_mode === 'source_coverage') this._load_source_coverage();
    }
  }

  // IT-F2-367 — Employee view mode. Loads F2-customer Exchange_Agreements
  // rows via GET /admin/employees; renders in a simple table (not the
  // ag-grid) with edit + delete inline actions. "Add Employee" button
  // opens the modal below for a fresh row.
  async _load_employees() {
    const r: any = await this.adminService.listEmployeeAgreements('f2');
    if (r && !r.err) this.employee_rows = r.rows || [];
  }

  openNewEmployeeModal() {
    this.is_new_employee = true;
    this.editing_employee = {
      user_id: '',
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      occupation: '',
      job_title: '',
      employment_function: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'US',
      pro_access: true,
      customer: 'f2',
      activation_date: '',
      deactivation_date: '',
      employer: {
        name: this.company_info?.legal_name || '',
        address: this.company_info?.address || '',
        city: this.company_info?.city || '',
        state: this.company_info?.state || '',
        zip_code: this.company_info?.zip_code || '',
        country: this.company_info?.country || 'US',
      },
    };
    this.show_employee_modal = true;
  }

  openEditEmployeeModal(row: any) {
    this.is_new_employee = false;
    // Deep-clone so cancel doesn't leak edits.
    this.editing_employee = JSON.parse(JSON.stringify(row));
    if (!this.editing_employee.employer) this.editing_employee.employer = {};
    // Coerce date fields to YYYY-MM-DD for the <input type="date"> binding.
    // Backend accepts YYYY-MM-DD or ISO; the input requires the short form.
    this.editing_employee.activation_date = this._to_ymd(this.editing_employee.activation_date);
    this.editing_employee.deactivation_date = this._to_ymd(this.editing_employee.deactivation_date);
    this.show_employee_modal = true;
  }

  private _to_ymd(v: any): string {
    if (!v) return '';
    const s = String(v);
    // Already YYYY-MM-DD → passthrough
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // ISO → slice first 10 chars
    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
    // Fallback: parse via Date
    const d = new Date(s);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  closeEmployeeModal() {
    this.show_employee_modal = false;
  }

  async saveEmployee() {
    if (!this.editing_employee.user_id) {
      this.toast.error('user_id required', 'Enter the user_id (typically email) before saving.');
      return;
    }
    const r: any = await this.adminService.upsertEmployeeAgreement(this.editing_employee);
    if (r?.err) {
      this.toast.error('Save failed', r.err);
      return;
    }
    this.toast.success('Saved', `Employee agreement upserted (${this.editing_employee.user_id})`);
    await this._load_employees();
    this.closeEmployeeModal();
  }

  async deleteEmployee(row: any) {
    if (!window.confirm(`Delete employee agreement for ${row.user_id}?\n\nThis removes the Exchange_Agreements row entirely. Cannot be undone.`)) return;
    const r: any = await this.adminService.deleteEmployeeAgreement(row.user_id, row.customer || 'f2');
    if (r?.err) {
      this.toast.error('Delete failed', r.err);
      return;
    }
    this.toast.success('Deleted', `${row.user_id} removed`);
    await this._load_employees();
  }

  // IT-F2-426 checklist 3b879378 helpers.
  on_lock_to_month_change(v: boolean) {
    this.lock_to_month = !!v;
    if (this.lock_to_month) {
      const fromYm = this.filter_month_from.value || '';
      // FormControl.disable() prevents user interaction while still
      // letting programmatic setValue continue to fire valueChanges.
      // emitEvent:false so we don't spurious-fire the mirror twice.
      this.filter_month_to.disable({ emitEvent: false });
      if (this.filter_month_to.value !== fromYm) {
        this.filter_month_to.setValue(fromYm);   // → _apply_month_range → reload
      } else {
        this._apply_month_range();
      }
    } else {
      this.filter_month_to.enable({ emitEvent: false });
    }
  }
  /** Options for the "To month" dropdown. When the lock is OFF and a
   *  From is picked, filter out months earlier than From so the operator
   *  can't invert the range. When lock is ON we don't render the dropdown
   *  at all (see template), so the full list is fine as a fallback. */
  get to_month_options(): { id: string; label: string }[] {
    if (this.lock_to_month) return this.month_options;
    const fromYm = this.filter_month_from.value || '';
    if (!fromYm) return this.month_options;
    return this.month_options.filter(o => o.id >= fromYm);
  }

  private _seed_month_options() {
    const now = DateTime.now();
    // 5 years back through 3 months forward (63 total). Reverse so
    // most recent appears at top of dropdown.
    const opts: { id: string; label: string }[] = [];
    for (let i = -3; i < 60; i++) {
      const d = now.plus({ months: -i });
      opts.push({ id: d.toFormat('yyyy-MM'), label: d.toFormat('LLLL yyyy') });
    }
    this.month_options = opts;
  }

  // Expands the month-range picker to date_from + date_to that backend
  // filters on. Either end can be blank (open-ended). When from > to
  // (misordered pick), swap silently rather than emit an invalid range.
  private _apply_month_range() {
    const from_ym = this.filter_month_from.value || '';
    const to_ym = this.filter_month_to.value || '';
    let date_from = '';
    let date_to = '';
    if (from_ym || to_ym) {
      let from_d = from_ym ? DateTime.fromFormat(from_ym, 'yyyy-MM') : null;
      let to_d = to_ym ? DateTime.fromFormat(to_ym, 'yyyy-MM') : null;
      if (from_d && to_d && from_d > to_d) {
        const tmp = from_d; from_d = to_d; to_d = tmp;
      }
      date_from = from_d?.isValid ? from_d.toFormat('yyyy-MM-dd') : '';
      date_to = to_d?.isValid ? to_d.endOf('month').toFormat('yyyy-MM-dd') : '';
    }
    // Set BOTH silently (no per-control valueChanges), then fire ONE
    // load_report. Otherwise each setValue fires load_report reading
    // the OTHER control's stale value → backend gets a request with
    // an empty date_to and returns the full 1641-user universe.
    // Mike caught 2026-09-02.
    const changed_from = this.filter_date_from.value !== date_from;
    const changed_to = this.filter_date_to.value !== date_to;
    if (changed_from) this.filter_date_from.setValue(date_from, { emitEvent: false });
    if (changed_to) this.filter_date_to.setValue(date_to, { emitEvent: false });
    if (changed_from || changed_to) {
      this.load_report();
      // IT-F2-426 4d5fb640 (Mike 2026-09-25 c/82a9fc85): month-picker
      // changes call setValue with emitEvent:false, so the date_from/to
      // valueChanges hooks that fire the tab-specific loads never trigger.
      // Fire them here directly so Report / Source Coverage tabs refresh
      // when the operator picks a different month range.
      if (this.view_mode === 'report_page') this._load_report_page();
      else if (this.view_mode === 'source_coverage') this._load_source_coverage();
    }
  }

  async load_report() {
    const query: any = {};
    const cust = this.filter_customers.value;
    if (cust && cust.length > 0) query.customers = cust;
    if (this.filter_date_from.value) query.date_from = this.filter_date_from.value;
    if (this.filter_date_to.value) query.date_to = this.filter_date_to.value;
    if (this.filter_date_field.value) query.date_field = this.filter_date_field.value;

    this._sync_url_from_filters();

    // IT-F2-426 b86d9456: fetch Delayed monthly counts in parallel so
    // _rebuild_monthly_counts can merge them synchronously. Customers-
    // scoped only; date range does NOT scope the count semantics — the
    // per-month aggregation dedups within each ym window bucket. Passing
    // just ym_from/ym_to (from the month picker) trims the RESPONSE to
    // the visible months without affecting per-month counts.
    const delayed_query: any = {};
    if (cust && cust.length > 0) delayed_query.customers = cust;
    if (this.filter_month_from.value) delayed_query.ym_from = this.filter_month_from.value;
    if (this.filter_month_to.value)   delayed_query.ym_to   = this.filter_month_to.value;

    // IT-F2-426 2e8a3714 (Mike 2026-09-28 c/9f324d58): also fetch an
    // UNBOUNDED exhibit-b for the Counts by Month aggregation. Same
    // customers + date_field but no date_from/date_to so the row set
    // (and therefore Feb's count) doesn't change when the operator
    // widens or narrows the visible window.
    const monthly_query: any = { ...query };
    delete monthly_query.date_from;
    delete monthly_query.date_to;

    const [res, delayed_res, monthly_res]: any[] = await Promise.all([
      this.adminService.getExhibitBReport(query),
      this.adminService.getDelayedMonthlyCounts(delayed_query),
      this.adminService.getExhibitBReport(monthly_query),
    ]);
    this.rows = (res && !res.err && Array.isArray(res.rows)) ? res.rows : [];
    this.monthly_source_rows = (monthly_res && !monthly_res.err && Array.isArray(monthly_res.rows)) ? monthly_res.rows : [];
    this._delayed_by_month = {};
    if (delayed_res && !delayed_res.err && Array.isArray(delayed_res.rows)) {
      for (const r of delayed_res.rows) this._delayed_by_month[r.MONTH] = r.DELAYED || 0;
    }
    this._rebuild_monthly_counts();
  }

  // IT-F2-391 c/a4c363b1 (2026-09-15) — Review workflow moved to
  // ExchangeAgreementReviewComponent at /admin/exchange-agreement-review.
  // The former _rebuild_pending_review_rows / on_review_filter_change /
  // is_*_disabled / _pending_row_key / on_approve_review* /
  // on_decline_review / open_view_application / close_view_application /
  // copy_agreement_json / agreement_question_labels / render_yes_no /
  // status_chip_for / get_review_reason / set_review_reason /
  // is_review_busy methods all live there now.

  // Group rows by month of the ROW FIELD that matches the current
  // date-field picker (Mike ask 2026-09-01 c/f3cf35e7). So when date
  // field = "Deactivation date", the monthly view counts users by
  // month of DEACTIVATION_DATE; when "First Signed" or "Activated",
  // it counts by EARLIEST_DATE. Source rows come from the grid-filtered
  // users grid (ag-grid column filters narrow it too). In 'monthly_counts'
  // view the grid holds counts (not users) so we can't read filtered users
  // rows from the grid — fall back to this.rows which reflects the backend
  // query result post filter-change.
  //
  // IT-F2-426 (Mike 2026-09-24 checklist 91c1028b): "The Counts by month
  // should refresh when the filters are changed". Previously this method
  // early-returned in monthly_counts view — so filter changes hit
  // load_report → this.rows updated → but monthly_counts_rows stayed
  // stale and the grid never re-rendered. Removed the early-return;
  // source now falls back to this.rows when the grid is showing counts,
  // and we push the fresh counts into the grid rowData if that's the
  // view we're on.
  private _rebuild_monthly_counts() {
    const inMonthlyView = this.view_mode === 'monthly_counts';
    // IT-F2-426 2e8a3714: monthly_source_rows is the STABLE, unbounded-
    // window exhibit-b response. Iterating it (instead of this.rows,
    // which is scoped to the Users view's date filter) keeps Feb's count
    // consistent across Feb-only vs Feb-Mar filter windows. Users view
    // switches (which read grid-filtered rows) still use this.rows so
    // ag-grid column filters can narrow the monthly counts too.
    const source = inMonthlyView
      ? (this.monthly_source_rows.length ? this.monthly_source_rows : this.rows)
      : ((this.grid?.api) ? this._get_filtered_rows() : this.rows);

    // IT-F2-426 (Mike 2026-09-24 clarification e265b763 → option A +
    // c/a8dcebce): per-month active-user headcount branch. Covers BOTH:
    // - active_in_window (backend returns rows overlapping the filter
    //   window; we bucket by "user active in month M");
    // - login_periods (backend returns one row per (user, period); we
    //   bucket the SAME way — a period's activation month is not what
    //   the operator wants to see when they filter March 2024 and get
    //   Jan/Feb buckets because those periods activated then).
    // Both are snapshot-count semantics, not flow-events, so CUMULATIVE
    // becomes running peak (max headcount so far).
    const dfv = this.filter_date_field.value;
    if (dfv === 'active_in_window' || dfv === 'login_periods') {
      this.monthly_counts_rows = this._compute_active_in_window_monthly(source);
      if (inMonthlyView && this.grid?.api) {
        this.grid.api.setGridOption('rowData', this.monthly_counts_rows);
      }
      return;
    }

    const row_field = this._monthly_group_field();
    // Dedup by user first — in login_periods mode a user has multiple
    // rows (one per period) and each row's EARLIEST_DATE = that period's
    // activation. Counting all rows double-counts users. Keep the row
    // with the MIN row_field per (USER_ID, CUSTOMER) so we count each
    // user once, in the month they first signed / activated / etc.
    // Mike ask 2026-09-02: "counts by month needs to be unique users
    // the first signed in, it looks double counted".
    const first_per_user = new Map<string, any>();
    for (const r of source) {
      const uid_key = `${r?.USER_ID_INTERNAL || r?.USER_ID || ''}|${r?.CUSTOMER || ''}`;
      const cur = first_per_user.get(uid_key);
      if (!cur) { first_per_user.set(uid_key, r); continue; }
      const cur_iso = cur?.[row_field] || '';
      const new_iso = r?.[row_field] || '';
      // Prefer non-empty over empty (would otherwise stick with null-
      // row since '2024-01' < '' is false), then MIN.
      if (!cur_iso && new_iso) first_per_user.set(uid_key, r);
      else if (cur_iso && new_iso && new_iso < cur_iso) first_per_user.set(uid_key, r);
    }
    // Track {pro, non_pro, pro_delayed, total} per month.
    type Bucket = { pro: number; non_pro: number; pro_delayed: number; total: number };
    const by_month = new Map<string, Bucket>();
    for (const r of first_per_user.values()) {
      const iso = r?.[row_field];
      if (!iso) continue;
      // IT-F2-426 (Mike 2026-09-24 c/0aafe1ad): month bucket in
      // America/New_York, NOT UTC. A row timestamped 2024-04-01T02:00Z is
      // April UTC but March 31st in ET — bucketing by UTC put it into
      // April and inflated Mike's single-month filter to two months.
      // Reports are ET-anchored per operator expectation.
      const d = DateTime.fromISO(iso, { zone: 'America/New_York' });
      if (!d.isValid) continue;
      const ym = d.toFormat('yyyy-MM');
      const b = by_month.get(ym) || { pro: 0, non_pro: 0, pro_delayed: 0, total: 0 };
      if (r.PRO_STATUS === 'Pro') b.pro++;
      else if (r.PRO_STATUS === 'Non-Pro') b.non_pro++;
      else if (r.PRO_STATUS === 'Pro-Delayed') b.pro_delayed++;
      b.total++;
      by_month.set(ym, b);
    }
    // IT-F2-426 b86d9456: merge Delayed counts from the login-source
    // sweep. Delayed users have no agreement so they don't show up in
    // by_month at all; we UNION the month keys and add DELAYED per row.
    const all_months = new Set<string>([...by_month.keys(), ...Object.keys(this._delayed_by_month)]);
    const sorted_ms = [...all_months].sort();
    let cum = 0;
    this.monthly_counts_rows = sorted_ms.map(m => {
      const b = by_month.get(m) || { pro: 0, non_pro: 0, pro_delayed: 0, total: 0 };
      const delayed = this._delayed_by_month[m] || 0;
      const total = b.total + delayed;
      cum += total;
      return { MONTH: m, PRO: b.pro, NON_PRO: b.non_pro, PRO_DELAYED: b.pro_delayed, DELAYED: delayed, TOTAL: total, CUMULATIVE: cum };
    });
    // IT-F2-426 (Mike checklist 91c1028b): if the operator is currently
    // viewing Counts by Month, push the fresh rows into ag-grid — Angular
    // change detection alone doesn't propagate the new array into the grid's
    // internal row model.
    if (inMonthlyView && this.grid?.api) {
      this.grid.api.setGridOption('rowData', this.monthly_counts_rows);
    }
  }

  /** IT-F2-426 clarification e265b763 → A: per-month active-user
   *  headcount for the "Active during window" date field. For each
   *  month M in the From/To range (or the observed range of the data
   *  when the picker is open-ended), count unique users whose
   *  (EARLIEST_DATE, DEACTIVATION_DATE) interval overlaps M. Deactivation
   *  blank == still active (no upper bound). Split by PRO_STATUS.
   *  Cumulative = running peak (max headcount so far). */
  private _compute_active_in_window_monthly(source: any[]): { MONTH: string; PRO: number; NON_PRO: number; PRO_DELAYED: number; DELAYED: number; TOTAL: number; CUMULATIVE: number }[] {
    // IT-F2-426 08a4a5da (Mike 2026-09-28): PER-PERIOD classification.
    // Old code deduped users up-front then took "most-permissive"
    // PRO_STATUS across all their period rows. That broke Pro-Delayed:
    // in the unbounded response a Pro user with periods spanning the
    // pro_live_data_start_date cutoff has one row with PRO_STATUS='Pro-
    // Delayed' (pre-cutoff period) and another 'Pro' (post-cutoff period).
    // The old dedup elevated them to Pro, so their Pro-Delayed
    // classification for months where only the pre-cutoff period was
    // active vanished (Feb 2025 Pro-Delayed dropped from 47 → 0).
    //
    // New shape: keep the PER-PERIOD rows, then for each walked month:
    //   1. Filter to periods that actually overlap that month.
    //   2. Group by user, pick most-permissive PRO_STATUS across the
    //      overlapping periods only (Pro > Pro-Delayed > Non-Pro).
    // So a Pro-Delayed period active in Feb still classifies the user
    // as Pro-Delayed for Feb even if they had a Pro period in July.
    // ET-anchored bucketing (c/0aafe1ad).
    // IT-F2-426 081e9cc5 (Mike 2026-09-28): user counts for Pro/Non-Pro/
    // Pro-Delayed only in months AT OR AFTER their agreement signing
    // (AGREEMENT_DATE = Exchange_Agreements.created_at, falling back to
    // EARLIEST_DATE if AGREEMENT_DATE isn't populated), and only while
    // their DEACTIVATION_DATE is still open (or absent). A user whose
    // login history reaches back before they signed the agreement does
    // NOT qualify for those pre-signing months — that traffic still
    // shows up as Delayed if it appears in a login source.
    type Period = { uid_key: string; signed_ym: string; end_ym: string; still_active: boolean; status: string };
    const periods: Period[] = [];
    for (const r of source) {
      const uid_key = `${r?.USER_ID_INTERNAL || r?.USER_ID || ''}|${r?.CUSTOMER || ''}`;
      // Mike c/081e9cc5 + c/f00d61cd 2026-09-28: "check their first
      // signed date or earliest exchange agreement date". Take the
      // MIN of the two so a user qualifies for a month if EITHER
      // predates it — EARLIEST_DATE is the "First Signed" field
      // already displayed on the Users grid (composite of admin
      // activation_date, login MIN, or agreement created_at);
      // AGREEMENT_DATE is the raw Exchange_Agreements.created_at.
      const agrIso  = String(r?.AGREEMENT_DATE || '');
      const earlIso = String(r?.EARLIEST_DATE  || '');
      const signedIso = (agrIso && earlIso)
        ? (agrIso < earlIso ? agrIso : earlIso)
        : (agrIso || earlIso);
      const endIso    = r?.DEACTIVATION_DATE || '';
      const signedYm = signedIso
        ? DateTime.fromISO(String(signedIso), { zone: 'America/New_York' }).toFormat('yyyy-MM')
        : '';
      const endYm = endIso
        ? DateTime.fromISO(String(endIso), { zone: 'America/New_York' }).toFormat('yyyy-MM')
        : '';
      if (!signedYm) continue;   // No signing date → can't anchor on month axis.
      periods.push({
        uid_key,
        signed_ym: signedYm,
        end_ym: endYm,
        still_active: !endYm,
        // IT-F2-426 2a77817b (Mike 2026-09-28): Pro/Non-Pro/Pro-Delayed only
        // come from an Exchange_Agreement submission. If PRO_STATUS is
        // missing here the row shouldn't be classified as any of them —
        // no-agreement users are Delayed and appear via the separate
        // /admin/delayed-monthly-* pipeline. Explicit '' status makes the
        // per-month classifier reduce to nothing for this row so no
        // fallback-to-Non-Pro leakage happens.
        status: String(r?.PRO_STATUS || ''),
      });
    }

    // Range of months to walk. Prefer the operator's From/To picker; else
    // fall back to the observed min-start → max-end across the data.
    let fromYm = String(this.filter_month_from.value || '').trim();
    let toYm = String(this.filter_month_to.value || '').trim();
    if (!fromYm || !toYm) {
      let minStart = '';
      let maxEnd = '';
      for (const p of periods) {
        if (p.signed_ym && (!minStart || p.signed_ym < minStart)) minStart = p.signed_ym;
        if (p.end_ym && (!maxEnd || p.end_ym > maxEnd)) maxEnd = p.end_ym;
      }
      if (!fromYm) fromYm = minStart;
      if (!toYm) toYm = maxEnd || DateTime.now().toFormat('yyyy-MM');
    }
    if (!fromYm || !toYm) return [];

    // Walk From → To inclusive.
    const rows: { MONTH: string; PRO: number; NON_PRO: number; PRO_DELAYED: number; DELAYED: number; TOTAL: number; CUMULATIVE: number }[] = [];
    const start = DateTime.fromFormat(fromYm, 'yyyy-MM', { zone: 'America/New_York' });
    const end   = DateTime.fromFormat(toYm, 'yyyy-MM', { zone: 'America/New_York' });
    if (!start.isValid || !end.isValid || end < start) return [];
    let peak = 0;
    for (let d = start; d <= end; d = d.plus({ months: 1 })) {
      const ym = d.toFormat('yyyy-MM');
      // Per-user status derived from overlapping periods only.
      // IT-F2-426 081e9cc5: only count a user if they had SIGNED an
      // agreement by month `ym` AND their deactivation hasn't passed.
      const status_by_user = new Map<string, string>();
      for (const p of periods) {
        if (p.signed_ym > ym) continue;   // signed after this month
        if (!p.still_active && p.end_ym && p.end_ym < ym) continue;   // deactivated before this month
        // period overlaps ym — merge status most-permissive.
        const cur = status_by_user.get(p.uid_key);
        if (!cur) status_by_user.set(p.uid_key, p.status);
        else if (p.status === 'Pro') status_by_user.set(p.uid_key, 'Pro');
        else if (p.status === 'Pro-Delayed' && cur !== 'Pro') status_by_user.set(p.uid_key, 'Pro-Delayed');
        else if (p.status === 'Non-Pro' && cur !== 'Pro' && cur !== 'Pro-Delayed') status_by_user.set(p.uid_key, 'Non-Pro');
      }
      let pro = 0, nonpro = 0, pro_delayed = 0;
      for (const s of status_by_user.values()) {
        if (s === 'Pro') pro++;
        else if (s === 'Pro-Delayed') pro_delayed++;
        else if (s === 'Non-Pro') nonpro++;
      }
      // IT-F2-426 b86d9456: Delayed users don't have Exchange_Agreement
      // intervals — fold in the per-month count from _delayed_by_month
      // (already deduped across sources by the backend).
      const delayed = this._delayed_by_month[ym] || 0;
      const total = pro + nonpro + pro_delayed + delayed;
      if (total > peak) peak = total;
      rows.push({ MONTH: ym, PRO: pro, NON_PRO: nonpro, PRO_DELAYED: pro_delayed, DELAYED: delayed, TOTAL: total, CUMULATIVE: peak });
    }
    return rows;
  }

  // Maps the date-field picker to the ROW field the monthly view groups
  // by. Deactivated → DEACTIVATION_DATE; everything else → EARLIEST_DATE
  // (First Signed, Activated, and Active-during-window all resolve to
  // the same first-access signal in practice).
  private _monthly_group_field(): string {
    return this.filter_date_field.value === 'deactivated' ? 'DEACTIVATION_DATE' : 'EARLIEST_DATE';
  }

  // IT-F2-426 (Mike 2026-09-24 checklist 72f22d44): tooltip text for the
  // Date-field picker. Explains what each option filters by so operators
  // don't have to guess which date the From/To month range applies to.
  date_field_help_tooltip(): string {
    const opt = this.filter_date_field.value;
    return (
      `The From/To month range is applied to this date on each row. Current pick: ` +
      this.date_field_option_tooltip(opt) +
      `\n\nAll options:\n` +
      `  • Active during window — user's scanner subscription overlapped ANY point in the range (union across scanners).\n` +
      `  • First Signed — the earliest Exchange Agreement signature date on record.\n` +
      `  • Activation date — when the user first became entitled to market data (first scanner grant).\n` +
      `  • Deactivation date — when their last-active scanner subscription ended.\n` +
      `  • Login usage periods — one row per user per usage period, computed from AuthLogins (main + legacy).`
    );
  }
  date_field_option_tooltip(id: string | null | undefined): string {
    switch (id) {
      case 'active_in_window': return 'Active during window — user\'s scanner subscription overlapped ANY point in the range.';
      case 'first_signed':     return 'First Signed — the earliest Exchange Agreement signature date on record.';
      case 'activated':        return 'Activation date — when the user first became entitled to market data.';
      case 'deactivated':      return 'Deactivation date — when the user\'s last-active scanner subscription ended.';
      case 'login_periods':    return 'Login usage periods — one row per (user, usage period) from AuthLogins (main + legacy).';
      default: return 'Which date the From/To month range applies to.';
    }
  }

  // IT-F2-426 (Mike 2026-09-24 checklist 739b08a0): plain-English sentence
  // rendered above the grid so a first-time viewer knows what they're
  // looking at. Tracks the active view_mode + date-field pick + customer
  // filter so the description stays honest as the operator scopes down.
  grid_summary_sentence(): string {
    const custs = this.filter_customers.value || [];
    const customerScope = custs.length === 0
      ? 'all customers'
      : (custs.length === 1
          ? `customer "${custs[0]}"`
          : `${custs.length} customers`);
    const fromYm = this.filter_month_from.value || '';
    const toYm = this.filter_month_to.value || '';
    let range = '';
    if (fromYm && toYm) range = ` between ${fromYm} and ${toYm} (ET)`;
    else if (fromYm)    range = ` from ${fromYm} (ET) onwards`;
    else if (toYm)      range = ` up to ${toYm} (ET)`;
    const dateFieldLabel = (this.date_field_options.find(o => o.id === this.filter_date_field.value)?.label || 'the selected date').toLowerCase();
    const rowCount =
      this.view_mode === 'monthly_counts'  ? `${this.monthly_counts_rows.length} month${this.monthly_counts_rows.length === 1 ? '' : 's'}` :
      this.view_mode === 'source_coverage' ? `${this.source_coverage_rows.length} row${this.source_coverage_rows.length === 1 ? '' : 's'}` :
      `${this.rows.length} row${this.rows.length === 1 ? '' : 's'}`;
    // IT-F2-426 c/2d14133a: call out ET anchoring in the description so
    // the operator sees the same time-zone story as the header tooltip.
    const tzNote = ' Month buckets are anchored in ET (America/New_York).';
    if (this.view_mode === 'monthly_counts') {
      return `Showing per-month counts of unique users (Pro / Non-Pro / Total / cumulative), keyed by ${dateFieldLabel}, for ${customerScope}${range}. ${rowCount} in current view.${tzNote}`;
    }
    if (this.view_mode === 'login_periods') {
      return `Showing one row per user per usage period (activation → deactivation) computed from AuthLogins main + legacy, for ${customerScope}${range}. ${rowCount} in current view.${tzNote}`;
    }
    // IT-F2-426 c/b89b32fe: Source Coverage — no date_field/date_range
    // apply (endpoint computes global coverage per source).
    if (this.view_mode === 'source_coverage') {
      return `Showing unique-user counts per (source database, month) across all 4 login sources (main+legacy AuthLogins + Graphem_Compliance + AuthLogins_Graphem) for ${customerScope}. Pro / Non-Pro split via Exchange_Agreements pro_access join (main wins over legacy). ${rowCount} in current view.${tzNote}`;
    }
    // users (default) view
    return `Showing one row per (user, customer) NYSE Exhibit B subscriber record (36 fields per §4.2.4/§4.2.5) plus internal review columns, filtered by ${dateFieldLabel} for ${customerScope}${range}. ${rowCount} in current view.${tzNote}`;
  }

  // Header label for the count column, matches the date-field pick.
  monthly_count_column_label(): string {
    const v = this.filter_date_field.value;
    if (v === 'deactivated') return 'Deactivation Count';
    if (v === 'activated') return 'Activation Count';
    // IT-F2-426 e265b763 + c/a8dcebce: both active_in_window and
    // login_periods use the per-month active-user headcount aggregator,
    // so both surface as "Active Users" instead of a flow-event count.
    if (v === 'active_in_window' || v === 'login_periods') return 'Active Users';
    return 'First-Signed Count';
  }

  // Click a monthly row → drill down to that month's users. Sets the
  // date range to the clicked YYYY-MM (aligned to the current date-field
  // pick, so if grouped by "Deactivation date" the filter applies to
  // that field too) and flips to Users view. valueChanges subs on the
  // date FormControls auto-refire the report. Mike ask 2026-09-01
  // c/a3b9bb3b — "click a row and show/copy/scroll the users in that month".
  on_monthly_row_click(evt: any) {
    // IT-F2-426 518a835e (Mike 2026-09-28 c/cf27c453): row-click drill
    // superseded by the per-cell drill-down modal (open_monthly_drill_modal).
    // Row-click USED to switch to Users view and rewrite filter_month_from /
    // filter_month_to / filter_date_field — which was firing simultaneously
    // with the cell-click modal-open, "jumbling the main report's filter
    // parameters". Silent no-op now for Counts by Month so only the modal
    // opens on click.
    if (this.view_mode !== 'monthly_counts') return;
  }

  set_view_mode(mode: 'users' | 'monthly_counts' | 'login_periods' | 'employees' | 'source_coverage' | 'report_page') {
    // Aggregate BEFORE flipping view_mode — _rebuild_monthly_counts
    // guards against running in monthly view (it would otherwise
    // re-aggregate itself). This ensures monthly_counts_rows reflects
    // the current grid-filtered users when the operator switches over.
    if (mode === 'monthly_counts' && this.view_mode === 'users') {
      this._rebuild_monthly_counts();
    }
    const previous_mode = this.view_mode;
    this.view_mode = mode;
    // Login-periods view forces the backend date_field to 'login_periods'
    // so the report emits one row per (user, period). Switching AWAY
    // restores the picker's previous value if it was different.
    if (mode === 'login_periods') {
      if (this.filter_date_field.value !== 'login_periods') {
        this.filter_date_field.setValue('login_periods');  // triggers load_report via valueChanges
      }
    }
    // Employees mode — separate CRUD list of F2 employee agreement rows.
    // Fetched from Exchange_Agreements via /admin/employees endpoint, not
    // the exhibit-b report pipeline. Fire-and-forget refresh on entry.
    if (mode === 'employees') {
      this._load_employees();
    }
    // IT-F2-426 (Mike c/b89b32fe + c/49315c17): Source Coverage view —
    // separate backend endpoint, fire-and-forget fetch on entry.
    if (mode === 'source_coverage') {
      this._load_source_coverage();
    }
    // IT-F2-426 checklist c0019445 (Mike c/6c21ee1e): Report page view —
    // parallel-fetches event counts + unique users for the two side-by-side
    // tables. Custom HTML tables (not ag-grid) so cell click handlers wire
    // straight to the drill-down modal.
    if (mode === 'report_page') {
      this._load_report_page();
    }
    // Grid column defs AND row data differ between modes. Employees +
    // report_page modes use custom tables in the HTML template, not the
    // ag-grid, so skip grid mutation.
    if (mode !== 'employees' && mode !== 'report_page' && this.grid?.api) {
      this.grid.api.setGridOption('columnDefs', this._current_column_defs());
      const rowData =
        mode === 'monthly_counts'  ? this.monthly_counts_rows :
        mode === 'source_coverage' ? this.source_coverage_rows :
        this.rows;
      this.grid.api.setGridOption('rowData', rowData);
    }
    // IT-F2-426 c845fcec (Mike 2026-09-25 c/7d335dbf): reflect the tab pick
    // in the URL so refresh preserves it. Silent no-op if only view_mode
    // changed and no filters — replaceUrl keeps history clean.
    if (previous_mode !== mode) this._sync_url_from_filters();
  }

  /** IT-F2-426 checklist c0019445 + ace10742 (Mike 2026-09-25 c/0ac828f9):
   *  fire both source-monthly aggregates in parallel — event counts +
   *  unique users. Shared customers filter AND the Date From/To range
   *  with the other tabs so operators can scope the Report tab to a
   *  specific window. */
  async _load_report_page() {
    const query: any = {};
    const cust = this.filter_customers.value;
    if (cust && cust.length > 0) query.customers = cust;
    if (this.filter_date_from.value) query.date_from = this.filter_date_from.value;
    if (this.filter_date_to.value)   query.date_to   = this.filter_date_to.value;
    if (this.filter_month_from.value) query.ym_from = this.filter_month_from.value;
    if (this.filter_month_to.value)   query.ym_to   = this.filter_month_to.value;
    this.report_page_loading = true;
    try {
      const [events_res, uniques_res] = await Promise.all([
        this.adminService.getAuthLoginsSourceMonthlyEvents(query),
        this.adminService.getAuthLoginsSourceMonthly(query),
      ]);
      this.source_events_rows  = (events_res  && !events_res.err  && Array.isArray(events_res.rows))  ? events_res.rows  : [];
      this.source_uniques_rows = (uniques_res && !uniques_res.err && Array.isArray(uniques_res.rows)) ? uniques_res.rows : [];
    } finally {
      this.report_page_loading = false;
    }
  }

  /** IT-F2-426 ace10742: copy an arbitrary source-monthly array (events
   *  or uniques) to clipboard as TSV, matching the visible column order
   *  including the Delayed column. Called from the Copy buttons in each
   *  panel title bar. */
  async copy_source_monthly_to_excel(kind: 'events' | 'uniques') {
    const rows = kind === 'events' ? this.source_events_rows : this.source_uniques_rows;
    if (!rows.length) {
      this.toast.info('Nothing to copy', `${kind === 'events' ? 'Event counts' : 'Unique users'} table is empty.`);
      return;
    }
    const header = ['SOURCE', 'MONTH', 'PRO', 'NON_PRO', 'PRO_DELAYED', 'DELAYED', 'TOTAL'];
    const tsv = [
      header.join('\t'),
      ...rows.map(r => [
        r.SOURCE || '',
        r.MONTH || '',
        String(r.PRO ?? 0),
        String(r.NON_PRO ?? 0),
        String(r.PRO_DELAYED ?? 0),
        String(r.DELAYED ?? 0),
        String(r.TOTAL ?? 0),
      ].join('\t')),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      this.toast.success('Copied', `${rows.length} ${kind === 'events' ? 'event-count' : 'unique-user'} rows on clipboard as TSV.`);
    } catch (e: any) {
      console.log('copy_source_monthly_to_excel() =>', e);
      this.toast.error('Clipboard blocked', e?.message || 'browser rejected the copy');
    }
  }

  /** IT-F2-426 checklist c0019445: click a numeric cell in either
   *  side-by-side table → open the drill-down modal with the list of
   *  contributing users + how the number was computed. `bucket` picks
   *  which count in the row was clicked (PRO / NON_PRO / PRO_DELAYED /
   *  TOTAL) — the modal filters its user list to match. */
  async open_drill_modal(source: string, month: string, kind: 'events' | 'uniques', bucket: 'PRO' | 'NON_PRO' | 'PRO_DELAYED' | 'DELAYED' | 'TOTAL') {
    this.drill_context = 'source_monthly';
    this.drill_source = source;
    this.drill_month = month;
    this.drill_kind = kind;
    this.drill_bucket = bucket;
    const bucketLabel =
      bucket === 'PRO_DELAYED' ? 'Pro-Delayed' :
      bucket === 'DELAYED'     ? 'Delayed' :
      bucket === 'NON_PRO'     ? 'Non-Pro' :
      bucket === 'PRO'         ? 'Pro' :
      '';
    this.drill_title = `${source} — ${month} — ${kind === 'events' ? 'Event counts' : 'Unique users'}${bucketLabel ? ` — ${bucketLabel}` : ''}`;
    this.drill_open = true;
    this.drill_loading = true;
    this.drill_rows = [];
    this.drill_totals = { PRO: 0, NON_PRO: 0, PRO_DELAYED: 0, DELAYED: 0, EVENTS: 0, UNIQUES: 0 };
    const query: any = { source, month };
    const cust = this.filter_customers.value;
    if (cust && cust.length > 0) query.customers = cust;
    const res: any = await this.adminService.getAuthLoginsSourceMonthlyDetail(query);
    this.drill_loading = false;
    if (!res || res.err) return;
    // IT-F2-426 be719bb6 (Mike 2026-09-30): the source-monthly drill
    // context has a single source (the picked `source` param). Stamp
    // it onto every row so the drill grid's SOURCE column populates
    // consistently across drill flows (agreement-backed + delayed +
    // source-monthly).
    const raw_rows: any[] = Array.isArray(res.rows) ? res.rows : [];
    this.drill_rows = raw_rows.map(r => ({ ...r, SOURCE: r.SOURCE || source }));
    this.drill_totals = res.totals || this.drill_totals;
    this.drill_source_kind = res.source_kind || 'authlogins';
  }

  close_drill_modal() {
    this.drill_open = false;
    this.drill_rows = [];
  }

  /** IT-F2-426 18d4b626: build the drill grid columnDefs. Grid renders
   *  inside the drill modal — full ag-grid so operators can filter /
   *  sort / resize while troubleshooting login usage. */
  _build_drill_grid_column_defs(): any[] {
    const fmt_et = (p: any) => {
      if (!p?.value) return '';
      const utc = DateTime.fromISO(String(p.value), { zone: 'utc' });
      return utc.isValid ? utc.setZone('America/New_York').toFormat('yyyy-MM-dd HH:mm') : '';
    };
    // IT-F2-426 4628b387 + fc3e745e (Mike 2026-09-30): source-aware
    // formatter for FIRST_SIGNED / FIRST_EVENT / LAST_EVENT columns.
    // Date-only fields upstream (admin activation_date ET-shifted to
    // ET-midnight, Graphem month-precision rows anchored at noon UTC)
    // were rendering as "2024-04-01 00:00 ET" or "2024-03-01 08:00 ET"
    // which looked like real timestamps. Rule: if row.SOURCE has NO
    // AuthLogins (main/legacy) entry → date-only display; otherwise
    // full HH:MM display (real login timestamp).
    const fmt_et_smart = (p: any) => {
      if (!p?.value) return '';
      const utc = DateTime.fromISO(String(p.value), { zone: 'utc' });
      if (!utc.isValid) return '';
      const et = utc.setZone('America/New_York');
      const src = String(p?.data?.SOURCE || '');
      const hasRealTimestampSrc = /AuthLogins \((main|legacy)\)/.test(src);
      return hasRealTimestampSrc
        ? et.toFormat('yyyy-MM-dd HH:mm')
        : et.toFormat('yyyy-MM-dd');
    };
    return [
      { field: 'EMAIL', headerName: 'Email', minWidth: 240, sortable: true, resizable: true, filter: 'agTextColumnFilter',
        cellStyle: { 'font-family': 'monospace', 'font-size': '11px' } },
      { field: 'CUSTOMER', headerName: 'Customer', minWidth: 140, sortable: true, resizable: true, filter: 'agSetColumnFilter' },
      { field: 'PRO_STATUS', headerName: 'Status', minWidth: 130, sortable: true, resizable: true, filter: 'agSetColumnFilter',
        cellStyle: (p: any) => p.value === 'Pro-Delayed' ? { color: '#f59e0b', 'font-weight': 'bold' }
                             : p.value === 'Delayed' ? { color: '#9ca3af', 'font-style': 'italic' } : {} },
      { field: 'EVENT_COUNT', headerName: 'Events', minWidth: 100, sortable: true, resizable: true, filter: 'agNumberColumnFilter',
        cellStyle: { 'text-align': 'right', 'font-family': 'monospace' },
        headerTooltip: `AuthLogins event count. Only meaningful for source-monthly drill (Report tab) and Delayed users where the row aggregates real login events; agreement-backed drill rows (Counts by Month Pro/Non-Pro/Pro-Delayed) always show 1 because Exchange_Agreements is per-user, not per-event.` },
      { field: 'AGREEMENT_DATE', headerName: 'Agreement Date (ET)', minWidth: 170, sortable: true, resizable: true, filter: 'agTextColumnFilter',
        valueFormatter: fmt_et,
        headerTooltip: `When the user submitted their Exchange_Agreement (Exchange_Agreements.created_at). Blank for Delayed users (no agreement on file).` },
      { field: 'FIRST_SIGNED', headerName: 'First Signed (ET)', minWidth: 170, sortable: true, resizable: true, filter: 'agTextColumnFilter',
        valueFormatter: fmt_et_smart,
        headerTooltip: `Same value as the "First Signed" column in the Users grid — the user's earliest activation date (login-derived MIN activation, unless overridden by admin-set activation_date). Rendered as date-only when the row has no real-timestamp login source; date + time when it does.` },
      { field: 'FIRST_EVENT', headerName: 'First Event (ET)', minWidth: 160, sortable: true, resizable: true, filter: 'agTextColumnFilter',
        valueFormatter: fmt_et_smart,
        headerTooltip: `First event date/time. Rendered date-only for date-precision sources (Graphem noon-UTC anchor, admin activation_date ET-shifted to midnight); full HH:MM for AuthLogins real timestamps.` },
      { field: 'LAST_EVENT', headerName: 'Last Event (ET)', minWidth: 160, sortable: true, resizable: true, filter: 'agTextColumnFilter',
        valueFormatter: fmt_et_smart,
        cellRenderer: (p: any) => {
          const formatted = fmt_et_smart(p);
          return formatted ? formatted : (p?.data?.STILL_ACTIVE ? '<span style="color:#86efac">still active</span>' : '');
        },
        headerTooltip: `Period deactivation (last login month max_date). Rendered date-only for date-precision sources; full HH:MM for AuthLogins real timestamps. "still active" means the user's login run reaches into the current calendar month — no bounded end.` },
      { field: 'STILL_ACTIVE', headerName: 'Still Active', minWidth: 110, sortable: true, resizable: true, filter: 'agSetColumnFilter',
        cellStyle: (p: any) => p.value ? { color: '#86efac' } : { color: '#9ca3af' },
        valueFormatter: (p: any) => p?.value ? 'yes' : 'no',
        headerTooltip: `TRUE = the user's login-derived period has no bounded end (last login was in the current calendar month per _compute_periods_from_month_map). These users classify as active for every month from FIRST_EVENT forward until their next login run breaks.` },
      // IT-F2-426 7aec3dcd (Mike 2026-09-29): USER_ID column for cross-
      // referencing the Non-Pro Entitlement Report (which keys on
      // Users.username). Populated for agreement-backed drill rows
      // (Pro/Non-Pro/Pro-Delayed) from row.F2_USER_ID / row.USER_ID.
      // Blank for Delayed drill rows and source-monthly drill rows —
      // those come from AuthLogins-aggregated shapes keyed by email,
      // which don't carry a Users.username through the aggregation.
      { field: 'USER_ID', headerName: 'User ID', minWidth: 160, sortable: true, resizable: true, filter: 'agTextColumnFilter',
        cellStyle: { 'font-family': 'monospace', 'font-size': '11px' },
        headerTooltip: `Users.username — same field the Non-Pro Entitlement Report keys on. Blank for Delayed drill rows and source-monthly drill rows (aggregated by email; no user_id carried through).` },
      // IT-F2-426 be719bb6 (Mike 2026-09-30): SOURCE column so operators
      // can tell where each row's dates came from — helps spot admin
      // calendar dates that were ET-shifted from naive UTC midnight
      // vs. real login-event timestamps.
      //   - Agreement-backed rows: comma-list from row.SOURCES (login-
      //     period aggregation) — e.g. "AuthLogins (main), Graphem_Compliance".
      //     Blank when the agreement has no login history on file
      //     (implies admin-set activation_date drove EARLIEST_DATE).
      //   - Delayed rows: comma-list from backend delayed-detail aggregation.
      //   - Source-monthly drill rows: the picked source (single value).
      { field: 'SOURCE', headerName: 'Source', minWidth: 200, sortable: true, resizable: true, filter: 'agTextColumnFilter',
        cellStyle: { 'font-family': 'monospace', 'font-size': '11px' },
        headerTooltip: `Data source(s) that contributed dates to this row. Blank on an agreement-backed row means no login history — dates came from admin activation_date / deactivation_date (ET calendar days shifted to ET-midnight at read time).` },
      {
        // IT-F2-426 51ceee70 (Mike 2026-09-28): action column pinned right
        // so the "View Logins" button stays visible even when the drill
        // grid's other columns overflow the modal width.
        colId: 'actions',
        headerName: 'Actions',
        pinned: 'right',
        width: 140, minWidth: 130, maxWidth: 150,
        sortable: false, filter: false, resizable: false, suppressMovable: true,
        floatingFilter: false,
        cellStyle: { 'text-align': 'center' },
        cellRenderer: (p: any) => p?.data?.EMAIL
          ? `<button class="dark-btn text-xs px-2 py-0.5" title="Show this user's AuthLogins history for the drill's month"><i class="fa-solid fa-clock-rotate-left mr-1 dark:text-sky-400"></i>View Logins</button>`
          : '',
        onCellClicked: (evt: any) => {
          const row = evt?.data;
          if (row?.EMAIL) {
            // IT-F2-426 d0e59c93: pass drill_month so login history scopes
            // to the same month the drill was for. Falls back to global if
            // no drill context (drill_month may be blank in source_monthly).
            const scopeMonth = this.drill_context === 'counts_by_month' ? this.drill_month : this.drill_month;
            this.open_user_login_history(row.EMAIL, row.CUSTOMER, scopeMonth);
          }
        },
      },
    ];
  }

  drill_grid_options: any = {
    context: this,
    suppressCellFocus: true,
    enableBrowserTooltips: true,
    defaultColDef: { floatingFilter: true, filter: true },
    // Row IDs so ag-grid keeps state across drill_filtered_rows() churn.
    getRowId: (params: any) => `${params.data?.EMAIL || ''}|${params.data?.CUSTOMER || ''}`,
    // IT-F2-426 0b90a56a (Mike 2026-09-30): row selection so operators
    // can pick specific users to include in Copy Debug. Checkbox on the
    // first data column + header checkbox for select-all.
    rowSelection: { mode: 'multiRow', checkboxes: true, headerCheckbox: true },
  };

  on_drill_grid_ready(e: any) {
    this.drill_grid_api = e.api;
    // Apply the current bucket filter as a set filter on PRO_STATUS.
    this._apply_drill_bucket_filter();
  }

  _apply_drill_bucket_filter() {
    if (!this.drill_grid_api) return;
    const want =
      this.drill_bucket === 'PRO' ? ['Pro'] :
      this.drill_bucket === 'NON_PRO' ? ['Non-Pro'] :
      this.drill_bucket === 'PRO_DELAYED' ? ['Pro-Delayed'] :
      this.drill_bucket === 'DELAYED' ? ['Delayed'] :
      null;   // TOTAL → clear the filter
    const model = this.drill_grid_api.getFilterModel() || {};
    if (want) {
      model.PRO_STATUS = { filterType: 'set', values: want };
    } else {
      delete model.PRO_STATUS;
    }
    this.drill_grid_api.setFilterModel(model);
  }

  /** IT-F2-426 18d4b626 + d0e59c93 (Mike 2026-09-28): open nested modal
   *  showing this user's AuthLogins history (main + legacy). When called
   *  from a Counts by Month drill row, `month` scopes the query so the
   *  operator sees only the logins that contributed to that drill count.
   *  Otherwise falls back to the report's current customers + date range. */
  async open_user_login_history(email: string, customer?: string, month?: string) {
    this.logins_open = true;
    this.logins_loading = true;
    this.logins_email = email;
    // IT-F2-426 c4c034c0 (Mike 2026-09-28): the drill row's CUSTOMER is
    // the user's agreement-customer slug but the compliance report is
    // FLEET-WIDE unless the operator narrows customers via the top-bar
    // filter. Passing customer=<drill-user's customer> to the login
    // history was hardwiring a fleet-narrowing that Mike doesn't want.
    //
    // Rule: only pass customer to the login history query when the top-
    // bar customers filter is a single value that matches the drill
    // customer (i.e., the operator explicitly narrowed to that customer).
    // Otherwise leave customer unset so we get the true fleet-wide
    // history for this email. Display keeps showing the drill customer
    // as context so it's clear which drill row opened the modal.
    this.logins_customer = customer || '';
    this.logins_month = month || '';
    this.logins_rows = [];
    this.logins_totals = { rows: 0, main: 0, legacy: 0, graphem: 0, al_graphem: 0 };
    const filter_customers = this.filter_customers.value || [];
    const explicit_customer_narrow = customer && filter_customers.length === 1 && filter_customers[0] === customer;
    const q: any = { email, limit: 500 };
    if (explicit_customer_narrow) q.customer = customer;
    if (month && /^\d{4}-\d{2}$/.test(month)) {
      const [y, m] = month.split('-').map(Number);
      const last = new Date(Date.UTC(y, m, 0));
      q.date_from = `${month}-01`;
      q.date_to = `${month}-${String(last.getUTCDate()).padStart(2, '0')}`;
    } else {
      if (this.filter_date_from.value) q.date_from = this.filter_date_from.value;
      if (this.filter_date_to.value)   q.date_to   = this.filter_date_to.value;
    }
    const res: any = await this.adminService.getUserLoginHistory(q);
    this.logins_loading = false;
    if (!res || res.err) return;
    this.logins_rows = Array.isArray(res.rows) ? res.rows : [];
    this.logins_totals = res.totals || this.logins_totals;
    (this as any)._last_logins_query_filter = res.query_filter || null;
    (this as any)._last_logins_diagnostic_probe = res.diagnostic_probe || null;
  }

  close_logins_modal() {
    this.logins_open = false;
    this.logins_rows = [];
  }

  /** Login-history grid column defs. Timestamp in ET (America/New_York). */
  _build_logins_grid_column_defs(): any[] {
    const fmt_et = (p: any) => {
      if (!p?.value) return '';
      const utc = DateTime.fromISO(String(p.value), { zone: 'utc' });
      return utc.isValid ? utc.setZone('America/New_York').toFormat('yyyy-MM-dd HH:mm:ss') : '';
    };
    return [
      { field: 'DATE', headerName: 'Date (ET)', minWidth: 170, sortable: true, resizable: true, filter: 'agTextColumnFilter', valueFormatter: fmt_et, sort: 'desc' },
      { field: 'SOURCE', headerName: 'Source', minWidth: 170, sortable: true, resizable: true, filter: 'agSetColumnFilter' },
      { field: 'CUSTOMER', headerName: 'Customer', minWidth: 140, sortable: true, resizable: true, filter: 'agSetColumnFilter' },
      { field: 'EMAIL', headerName: 'Email', minWidth: 240, sortable: true, resizable: true, filter: 'agTextColumnFilter', cellStyle: { 'font-family': 'monospace', 'font-size': '11px' } },
      { field: 'IP', headerName: 'IP', minWidth: 140, sortable: true, resizable: true, filter: 'agTextColumnFilter', cellStyle: { 'font-family': 'monospace' } },
      { field: 'PROVIDER', headerName: 'Provider', minWidth: 120, sortable: true, resizable: true, filter: 'agSetColumnFilter' },
      { field: 'USER_AGENT', headerName: 'User Agent', minWidth: 280, sortable: true, resizable: true, filter: 'agTextColumnFilter', tooltipField: 'USER_AGENT', cellStyle: { 'font-size': '10px' } },
    ];
  }

  logins_grid_options: any = {
    context: this,
    suppressCellFocus: true,
    enableBrowserTooltips: true,
    defaultColDef: { floatingFilter: true, filter: true },
  };

  on_logins_grid_ready(e: any) {
    this.logins_grid_api = e.api;
  }

  /** Copy the visible login-history rows to clipboard as TSV. */
  async copy_logins_to_excel() {
    const rows: any[] = [];
    if (this.logins_grid_api) this.logins_grid_api.forEachNodeAfterFilterAndSort((n: any) => rows.push(n.data));
    else rows.push(...this.logins_rows);
    if (!rows.length) return;
    const fmt_et = (iso: any) => {
      if (!iso) return '';
      const utc = DateTime.fromISO(String(iso), { zone: 'utc' });
      return utc.isValid ? utc.setZone('America/New_York').toFormat('yyyy-MM-dd HH:mm:ss') : '';
    };
    const header = ['Date (ET)', 'Source', 'Customer', 'Email', 'IP', 'Provider', 'User Agent'];
    const tsv = [
      header.join('\t'),
      ...rows.map(r => [fmt_et(r.DATE), r.SOURCE || '', r.CUSTOMER || '', r.EMAIL || '', r.IP || '', r.PROVIDER || '', r.USER_AGENT || ''].join('\t')),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      this.toast.success('Copied', `${rows.length} login row${rows.length === 1 ? '' : 's'} on clipboard`);
    } catch (e: any) {
      this.toast.error('Clipboard blocked', e?.message || 'browser rejected the copy');
    }
  }

  /** IT-F2-426 ea7782ad (Mike 2026-09-28): Copy Debug on the login history
   *  modal so operators can share the exact state (query params, totals,
   *  sample rows) with support instead of a screenshot. */
  async copy_logins_debug() {
    const rows: any[] = [];
    if (this.logins_grid_api) this.logins_grid_api.forEachNodeAfterFilterAndSort((n: any) => rows.push(n.data));
    else rows.push(...this.logins_rows);
    // IT-F2-426 b5a8027f + bbf69448 (Mike 2026-09-28): capture backend's
    // mongo filter + a top-level human-readable label so operators know
    // which drill / window this debug output is from at a glance.
    const originLabel =
      this.drill_context === 'counts_by_month'
        ? `Counts by Month drill · ${this.drill_month || '?'} · ${this.drill_bucket}`
        : this.drill_context === 'source_monthly'
          ? `${this.drill_source} · ${this.drill_month || '?'} · ${this.drill_kind} · ${this.drill_bucket}`
          : `Report ${this.view_mode}`;
    const snapshot: any = {
      timestamp: new Date().toISOString(),
      label: `Login history — ${this.logins_email}${this.logins_customer ? ' · ' + this.logins_customer : ''}${this.logins_month ? ' · ' + this.logins_month : ''} — from ${originLabel}`,
      url: window.location.href,
      backend_query_filter: (this as any)._last_logins_query_filter || null,
      backend_diagnostic_probe: (this as any)._last_logins_diagnostic_probe || null,
      context: {
        view_mode: this.view_mode,
        drill_context: this.drill_context,
        drill_source: this.drill_source,
        drill_month: this.drill_month,
        drill_bucket: this.drill_bucket,
      },
      report_filters: {
        customers: this.filter_customers.value,
        date_field: this.filter_date_field.value,
        month_from: this.filter_month_from.value,
        month_to: this.filter_month_to.value,
        date_from: this.filter_date_from.value,
        date_to: this.filter_date_to.value,
      },
      logins_query: {
        email: this.logins_email,
        customer: this.logins_customer,
        month: this.logins_month,
      },
      totals: this.logins_totals,
      row_count_after_grid_filter: rows.length,
      total_rows_loaded: this.logins_rows.length,
      sample_first_5: rows.slice(0, 5),
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
      this.toast.success('Debug snapshot copied', `${rows.length} rows visible / ${this.logins_rows.length} loaded`);
    } catch (e: any) {
      this.toast.error('Clipboard blocked', e?.message || 'browser rejected the copy');
    }
  }

  /** IT-F2-426 518a835e (Mike 2026-09-28 c/5b547985): open the drill
   *  modal from a Counts by Month cell click. For Pro / Non-Pro /
   *  Pro-Delayed / Total the contributing users are already in
   *  this.rows (Exchange_Agreements-driven) — filter client-side via
   *  the same activity-overlap logic as _compute_active_in_window_monthly.
   *  For Delayed, the users have no agreement so we call the backend. */
  async open_monthly_drill_modal(month: string, bucket: 'PRO' | 'NON_PRO' | 'PRO_DELAYED' | 'DELAYED' | 'TOTAL') {
    this.drill_context = 'counts_by_month';
    this.drill_source = 'Counts by Month';
    this.drill_month = month;
    this.drill_kind = 'uniques';
    this.drill_bucket = bucket;
    const bucketLabel =
      bucket === 'PRO_DELAYED' ? 'Pro-Delayed' :
      bucket === 'DELAYED'     ? 'Delayed' :
      bucket === 'NON_PRO'     ? 'Non-Pro' :
      bucket === 'PRO'         ? 'Pro' : '';
    this.drill_title = `Counts by Month — ${month}${bucketLabel ? ` — ${bucketLabel}` : ' — All'}`;
    this.drill_open = true;
    this.drill_loading = true;
    this.drill_rows = [];
    this.drill_totals = { PRO: 0, NON_PRO: 0, PRO_DELAYED: 0, DELAYED: 0, EVENTS: 0, UNIQUES: 0 };

    // IT-F2-426 d8bc5534 (Mike 2026-09-28): drill users must come from
    // the SAME source array the Counts by Month grid aggregated from —
    // otherwise the drill returns 0 for a bucket that the grid shows as
    // non-zero. monthly_source_rows is the unbounded universe; fall back
    // to this.rows if the unbounded fetch failed. Mirrors the per-month
    // classification in _compute_active_in_window_monthly.
    const drill_source = this.monthly_source_rows.length ? this.monthly_source_rows : this.rows;
    type PeriodRow = { uid_key: string; EMAIL: string; CUSTOMER: string; status: string; signed_ym: string; end_ym: string; still_active: boolean; FIRST_EVENT: string; LAST_EVENT: string; AGREEMENT_DATE: string; USER_ID: string; SOURCES: string };
    const periods: PeriodRow[] = [];
    for (const r of drill_source) {
      const uid_key = `${r?.USER_ID_INTERNAL || r?.USER_ID || ''}|${r?.CUSTOMER || ''}`;
      // Mike c/081e9cc5 + c/f00d61cd 2026-09-28: month-membership
      // anchor is MIN(EARLIEST_DATE, AGREEMENT_DATE). EARLIEST_DATE
      // is the Users-grid "First Signed" column; AGREEMENT_DATE is
      // the raw Exchange_Agreements.created_at. A user qualifies for
      // a month if EITHER date predates it.
      const startIso = r?.EARLIEST_DATE || '';
      const endIso   = r?.DEACTIVATION_DATE || '';
      const agreementIso = String(r?.AGREEMENT_DATE || r?.DATE_AND_TIME_STAMP || '');
      const firstSignedIso = String(startIso || '');
      const signedIso = (agreementIso && firstSignedIso)
        ? (agreementIso < firstSignedIso ? agreementIso : firstSignedIso)
        : (agreementIso || firstSignedIso);
      const signedYm = signedIso ? DateTime.fromISO(signedIso, { zone: 'America/New_York' }).toFormat('yyyy-MM') : '';
      if (!signedYm) continue;
      const endYm    = endIso   ? DateTime.fromISO(String(endIso),   { zone: 'America/New_York' }).toFormat('yyyy-MM') : '';
      periods.push({
        uid_key,
        EMAIL: String(r?.SUBSCRIBERS_EMAIL_ADDRESS || '').toLowerCase(),
        CUSTOMER: String(r?.CUSTOMER || ''),
        // IT-F2-426 2a77817b (Mike 2026-09-28): Pro/Non-Pro/Pro-Delayed only
        // come from an Exchange_Agreement submission. If PRO_STATUS is
        // missing here the row shouldn't be classified as any of them —
        // no-agreement users are Delayed and appear via the separate
        // /admin/delayed-monthly-* pipeline. Explicit '' status makes the
        // per-month classifier reduce to nothing for this row so no
        // fallback-to-Non-Pro leakage happens.
        status: String(r?.PRO_STATUS || ''),
        signed_ym: signedYm,
        end_ym: endYm,
        still_active: !endYm,
        FIRST_EVENT: startIso,
        LAST_EVENT: endIso,
        // IT-F2-426 6186ad68 (Mike 2026-09-28): agreement submission timestamp.
        AGREEMENT_DATE: agreementIso,
        // IT-F2-426 7aec3dcd (Mike 2026-09-29): USER_ID for cross-reference
        // with Non-Pro Entitlement Report (which keys on Users.username).
        USER_ID: String(r?.F2_USER_ID || r?.USER_ID || ''),
        // IT-F2-426 be719bb6 (Mike 2026-09-30): SOURCES so operators can
        // tell where a row's dates came from — helps spot ET-shifted
        // admin calendar dates vs. real login-event timestamps.
        SOURCES: String(r?.SOURCES || ''),
      });
    }
    // Per-month classification: filter periods overlapping `month`, then
    // reduce per-user to most-permissive status across overlapping periods.
    type MergedUser = { EMAIL: string; CUSTOMER: string; PRO_STATUS: string; FIRST_EVENT: string; LAST_EVENT: string; AGREEMENT_DATE: string; USER_ID: string; SOURCES: Set<string> };
    const per_user = new Map<string, MergedUser>();
    for (const p of periods) {
      if (p.signed_ym > month) continue;   // signed after month
      if (!p.still_active && p.end_ym && p.end_ym < month) continue;   // deactivated before month
      const cur = per_user.get(p.uid_key);
      const p_sources = p.SOURCES ? p.SOURCES.split(',').map(s => s.trim()).filter(Boolean) : [];
      if (!cur) {
        per_user.set(p.uid_key, {
          EMAIL: p.EMAIL,
          CUSTOMER: p.CUSTOMER,
          PRO_STATUS: p.status,
          FIRST_EVENT: p.FIRST_EVENT,
          LAST_EVENT: p.LAST_EVENT,
          AGREEMENT_DATE: p.AGREEMENT_DATE,
          USER_ID: p.USER_ID,
          SOURCES: new Set(p_sources),
        });
      } else {
        if (p.status === 'Pro') cur.PRO_STATUS = 'Pro';
        else if (p.status === 'Pro-Delayed' && cur.PRO_STATUS !== 'Pro') cur.PRO_STATUS = 'Pro-Delayed';
        else if (p.status === 'Non-Pro' && cur.PRO_STATUS !== 'Pro' && cur.PRO_STATUS !== 'Pro-Delayed') cur.PRO_STATUS = 'Non-Pro';
        if (p.FIRST_EVENT && (!cur.FIRST_EVENT || p.FIRST_EVENT < cur.FIRST_EVENT)) cur.FIRST_EVENT = p.FIRST_EVENT;
        if (p.LAST_EVENT && (!cur.LAST_EVENT || p.LAST_EVENT > cur.LAST_EVENT)) cur.LAST_EVENT = p.LAST_EVENT;
        if (p.AGREEMENT_DATE && (!cur.AGREEMENT_DATE || p.AGREEMENT_DATE < cur.AGREEMENT_DATE)) cur.AGREEMENT_DATE = p.AGREEMENT_DATE;
        if (p.USER_ID && !cur.USER_ID) cur.USER_ID = p.USER_ID;
        for (const s of p_sources) cur.SOURCES.add(s);
      }
    }

    const agreement_rows = [...per_user.values()].map(u => ({
      EMAIL: u.EMAIL,
      CUSTOMER: u.CUSTOMER,
      PRO_STATUS: u.PRO_STATUS,
      EVENT_COUNT: 1,
      FIRST_EVENT: u.FIRST_EVENT || undefined,
      LAST_EVENT: u.LAST_EVENT || undefined,
      AGREEMENT_DATE: u.AGREEMENT_DATE || undefined,
      // IT-F2-426 07082984 (Mike 2026-09-28): FIRST_SIGNED matches the
      // "First Signed" column in the Users grid — same value as
      // FIRST_EVENT for agreement-backed rows (row.EARLIEST_DATE at the
      // backend, which is MIN(login-derived earliest, admin-set
      // activation_date)). Duplicating the value under an explicit
      // FIRST_SIGNED alias so the drill matches Users-grid terminology.
      FIRST_SIGNED: u.FIRST_EVENT || undefined,
      // IT-F2-426 f5111f38 (Mike 2026-09-28): explicit still-active flag
      // so operators can see WHY a user with only a FIRST_EVENT (no
      // LAST_EVENT) appears in a later month's drill.
      STILL_ACTIVE: !u.LAST_EVENT,
      // IT-F2-426 7aec3dcd (Mike 2026-09-29): USER_ID for cross-reference
      // with the Non-Pro Entitlement Report (which keys on username).
      USER_ID: u.USER_ID || undefined,
      // IT-F2-426 be719bb6 (Mike 2026-09-30): SOURCE column so operators
      // can identify where each row's dates came from — helps spot
      // ET-shifted admin calendar dates vs. real login-event timestamps.
      SOURCE: u.SOURCES.size ? [...u.SOURCES].join(', ') : undefined,
    }));

    // Delayed users need a backend call — no client-side data on file.
    let delayed_rows: typeof this.drill_rows = [];
    if (bucket === 'DELAYED' || bucket === 'TOTAL') {
      const query: any = { month };
      const cust = this.filter_customers.value;
      if (cust && cust.length > 0) query.customers = cust;
      if (this.filter_date_from.value) query.date_from = this.filter_date_from.value;
      if (this.filter_date_to.value)   query.date_to   = this.filter_date_to.value;
      const res: any = await this.adminService.getDelayedMonthlyDetail(query);
      if (res && !res.err && Array.isArray(res.rows)) {
        delayed_rows = res.rows.map((r: any) => ({
          EMAIL: r.EMAIL || '',
          CUSTOMER: r.CUSTOMER || '',
          PRO_STATUS: 'Delayed',
          EVENT_COUNT: Number(r.EVENT_COUNT || 0),
          FIRST_EVENT: r.FIRST_SEEN || undefined,
          LAST_EVENT: r.LAST_SEEN || undefined,
          // IT-F2-426 be719bb6 (Mike 2026-09-30): backend delayed detail
          // already aggregates a comma-list of contributing sources.
          SOURCE: r.SOURCES || undefined,
        }));
      }
    }

    this.drill_rows = [...agreement_rows, ...delayed_rows];
    // Totals
    const t = { PRO: 0, NON_PRO: 0, PRO_DELAYED: 0, DELAYED: 0, EVENTS: 0, UNIQUES: this.drill_rows.length };
    for (const r of this.drill_rows) {
      t.EVENTS += r.EVENT_COUNT || 0;
      if (r.PRO_STATUS === 'Pro') t.PRO += 1;
      else if (r.PRO_STATUS === 'Pro-Delayed') t.PRO_DELAYED += 1;
      else if (r.PRO_STATUS === 'Delayed') t.DELAYED += 1;
      else t.NON_PRO += 1;
    }
    this.drill_totals = t;
    this.drill_loading = false;
  }

  /** ag-grid cell click handler for Counts by Month — routes numeric
   *  cells (PRO / NON_PRO / PRO_DELAYED / DELAYED / TOTAL) into the
   *  drill modal. */
  on_monthly_counts_cell_clicked(evt: any) {
    if (this.view_mode !== 'monthly_counts') return;
    const col: string = String(evt?.column?.getColId?.() || evt?.colDef?.field || '');
    const row = evt?.data;
    if (!row?.MONTH) return;
    if (col === 'PRO' || col === 'NON_PRO' || col === 'PRO_DELAYED' || col === 'DELAYED' || col === 'TOTAL') {
      this.open_monthly_drill_modal(row.MONTH, col as any);
    }
  }

  /** Drill modal rows currently visible after the bucket filter. */
  drill_filtered_rows() {
    if (this.drill_bucket === 'TOTAL') return this.drill_rows;
    const want =
      this.drill_bucket === 'PRO'         ? 'Pro' :
      this.drill_bucket === 'PRO_DELAYED' ? 'Pro-Delayed' :
      this.drill_bucket === 'DELAYED'     ? 'Delayed' :
      'Non-Pro';
    return this.drill_rows.filter(r => r.PRO_STATUS === want);
  }

  /** Copy drill modal rows to clipboard as TSV so a paste into Excel /
   *  Sheets works out of the box. Matches the shape rendered in the
   *  modal table so the operator sees the same data they paste. */
  /** IT-F2-426 6e3a0ac6 (Mike 2026-09-28): Copy Debug on the drill modal
   *  so operators can share the drill state (context, filters, bucket
   *  counts, sample rows) with a clear label at the top. */
  async copy_drill_debug() {
    // IT-F2-426 0b90a56a (Mike 2026-09-30): prefer explicitly-selected
    // rows so operators can pick which users to include in the debug
    // payload. Falls back to grid filter+sort output when nothing is
    // selected (prior behavior).
    const filtered: any[] = [];
    const selected = this.drill_grid_api ? (this.drill_grid_api.getSelectedRows() || []) : [];
    if (selected.length > 0) {
      filtered.push(...selected);
    } else if (this.drill_grid_api) {
      this.drill_grid_api.forEachNodeAfterFilterAndSort((n: any) => filtered.push(n.data));
    } else {
      filtered.push(...this.drill_filtered_rows());
    }
    const label =
      this.drill_context === 'counts_by_month'
        ? `Drill — Counts by Month · ${this.drill_month} · ${this.drill_bucket}`
        : `Drill — ${this.drill_source} · ${this.drill_month} · ${this.drill_kind} · ${this.drill_bucket}`;
    const snapshot = {
      timestamp: new Date().toISOString(),
      label,
      url: window.location.href,
      drill: {
        context: this.drill_context,
        source: this.drill_source,
        month: this.drill_month,
        kind: this.drill_kind,
        bucket: this.drill_bucket,
        title: this.drill_title,
      },
      report_filters: {
        customers: this.filter_customers.value,
        date_field: this.filter_date_field.value,
        month_from: this.filter_month_from.value,
        month_to: this.filter_month_to.value,
        date_from: this.filter_date_from.value,
        date_to: this.filter_date_to.value,
      },
      totals: this.drill_totals,
      counts: {
        drill_rows_total: this.drill_rows.length,
        after_grid_filter: filtered.length,
      },
      sample_first_5: filtered.slice(0, 5),
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
      this.toast.success('Debug snapshot copied', `${filtered.length} rows visible / ${this.drill_rows.length} loaded`);
    } catch (e: any) {
      this.toast.error('Clipboard blocked', e?.message || 'browser rejected the copy');
    }
  }

  async copy_drill_to_excel() {
    // IT-F2-426 18d4b626: prefer the ag-grid's filter+sort output so the
    // clipboard matches whatever the operator sees after column filters.
    const rows: any[] = [];
    if (this.drill_grid_api) {
      this.drill_grid_api.forEachNodeAfterFilterAndSort((n: any) => rows.push(n.data));
    } else {
      rows.push(...this.drill_filtered_rows());
    }
    if (!rows.length) return;
    const fmt_et = (iso: any) => {
      if (!iso) return '';
      const utc = DateTime.fromISO(String(iso), { zone: 'utc' });
      return utc.isValid ? utc.setZone('America/New_York').toFormat('yyyy-MM-dd HH:mm') : String(iso);
    };
    const header = ['EMAIL', 'CUSTOMER', 'PRO_STATUS', 'EVENT_COUNT', 'AGREEMENT_DATE (ET)', 'FIRST_SIGNED (ET)', 'FIRST_EVENT (ET)', 'LAST_EVENT (ET)', 'USER_ID', 'SOURCE'];
    const tsv = [
      header.join('\t'),
      ...rows.map(r => [
        r.EMAIL || '',
        r.CUSTOMER || '',
        r.PRO_STATUS || '',
        String(r.EVENT_COUNT ?? ''),
        fmt_et((r as any).AGREEMENT_DATE),
        fmt_et((r as any).FIRST_SIGNED),
        fmt_et(r.FIRST_EVENT),
        fmt_et(r.LAST_EVENT),
        (r as any).USER_ID || '',
        (r as any).SOURCE || '',
      ].join('\t')),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      this.toast.success('Copied', `${rows.length} row${rows.length === 1 ? '' : 's'} copied as TSV — paste into Excel / Sheets.`);
    } catch (e: any) {
      console.log('copy_drill_to_excel() =>', e);
      this.toast.error('Clipboard blocked', e?.message || 'browser rejected the copy');
    }
  }

  /** Human-readable calculation explainer shown in the modal header so
   *  the operator understands what the number means. Text tracks the
   *  source_kind + kind (events vs uniques) so the operator learns the
   *  dedup / bucketing rule for that specific source. */
  drill_calc_explanation(): string {
    // IT-F2-426 518a835e: Counts-by-Month drill has its own semantics.
    if (this.drill_context === 'counts_by_month') {
      return `Counts by Month drill: users active in ${this.drill_month} per the current date-field pick. Pro / Non-Pro / Pro-Delayed derived from Exchange_Agreements (client-side overlap of EARLIEST_DATE..DEACTIVATION_DATE, ET-anchored). Delayed = keys seen in a login source (AuthLogins main + legacy + Graphem_Compliance + AuthLogins_Graphem) with no Exchange_Agreement on file (fetched from /admin/delayed-monthly-detail).`;
    }
    const src = this.drill_source;
    const isAL = src === 'AuthLogins (main)' || src === 'AuthLogins (legacy)';
    if (isAL) {
      if (this.drill_kind === 'events') {
        return `AuthLogins raw event count: number of documents in F2-ADMIN.AuthLogins on ${src.includes('legacy') ? 'legacy (66.85.152.90)' : 'main'} mongo whose date field bucketed to ${this.drill_month} in ET (America/New_York), one row per (email, customer) with the per-user event count.`;
      }
      return `AuthLogins unique users: distinct (email.lower, customer) pairs from F2-ADMIN.AuthLogins on ${src.includes('legacy') ? 'legacy (66.85.152.90)' : 'main'} mongo whose date bucketed to ${this.drill_month} in ET. Pro / Non-Pro / Pro-Delayed derived by joining Exchange_Agreements.pro_access (main wins over legacy) then applying the Pro 15-min-delayed cutoff.`;
    }
    // Graphem
    if (this.drill_kind === 'events') {
      return `Graphem event count: number of rows in F2-ADMIN.${src} whose Month / Activation Date / First..Last Access Date span includes ${this.drill_month}. First..Last spans expand into one row per covered month, then de-duplicated per source per user per month.`;
    }
    return `Graphem unique users: distinct (email.lower, customer_slug) pairs from F2-ADMIN.${src} whose Month / Activation Date / First..Last Access Date span includes ${this.drill_month}.`;
  }

  /** IT-F2-426 c/b89b32fe: fetch per-database per-month unique-user
   *  counts from /admin/authlogins-source-monthly. Uses the same
   *  customers filter as the main report; date range doesn't apply
   *  here since we're reporting global coverage per source. */
  async _load_source_coverage() {
    const query: any = {};
    const cust = this.filter_customers.value;
    if (cust && cust.length > 0) query.customers = cust;
    const res: any = await this.adminService.getAuthLoginsSourceMonthly(query);
    this.source_coverage_rows = (res && !res.err && Array.isArray(res.rows)) ? res.rows : [];
    if (this.view_mode === 'source_coverage' && this.grid?.api) {
      this.grid.api.setGridOption('rowData', this.source_coverage_rows);
    }
  }

  private _current_column_defs(): any[] {
    // IT-F2-426 c/b89b32fe + c/49315c17: Source Coverage grid — one row
    // per (source, month) unique-user counts. Same PRO / NON_PRO / TOTAL
    // shape as Counts by Month. No CUMULATIVE — sources are independent.
    if (this.view_mode === 'source_coverage') {
      return [
        { field: 'MONTH', headerName: 'Month (YYYY-MM, ET)', minWidth: 170, sortable: true, filter: 'agTextColumnFilter',
          headerTooltip: `Calendar month (ET-anchored, America/New_York for AuthLogins; month-precise strings for Graphem sources). Rendered as YYYY-MM.` },
        { field: 'SOURCE', headerName: 'Source Database', minWidth: 220, sortable: true, filter: 'agTextColumnFilter',
          headerTooltip: `Which login-events collection contributed this row. "AuthLogins (main)" = live main-mongo AuthLogins. "AuthLogins (legacy)" = historical 66.85.152.90 AuthLogins. "Graphem_Compliance" + "AuthLogins_Graphem" = historical Graphem snapshot collections on main mongo (month-precise, no day info).` },
        { field: 'PRO', headerName: 'Pro Unique Users', minWidth: 150, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right' },
          headerTooltip: `Distinct Pro (professional) users this source recorded in this month. Dedup key: (email.lower, customer). Pro status resolved by joining against Exchange_Agreements.pro_access (main wins over legacy).` },
        { field: 'NON_PRO', headerName: 'Non-Pro Unique Users', minWidth: 180, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right' },
          headerTooltip: `Distinct Non-Pro (nonprofessional) users this source recorded in this month. Dedup key: (email.lower, customer). Requires an Exchange_Agreement with pro_access=false; users with NO agreement at all fall into the Delayed column instead.` },
        // IT-F2-426 c/cb897b64: Pro-Delayed column separated from Non-Pro.
        { field: 'PRO_DELAYED', headerName: 'Pro-Delayed Unique Users', minWidth: 210, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right', 'color': '#f59e0b' },
          headerTooltip: `Distinct non-employee Pros on 15-minute delayed data (Company Info → pro_15min_delayed=true, no cutoff or earliest date < cutoff). Broken out here so the Non-Pro column stays clean of Pros that just happen to not be receiving live data yet.` },
        // IT-F2-426 c/6d63f68d (Mike 2026-09-25): Delayed column — users
        // this source recorded who have NO Exchange_Agreement on file.
        // Distinct 4th class from Pro-Delayed; can't legally receive live
        // data because there's no consent record.
        { field: 'DELAYED', headerName: 'Delayed Unique Users', minWidth: 180, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right', 'color': '#9ca3af', 'font-style': 'italic' },
          headerTooltip: `Distinct users this source recorded who have NO Exchange_Agreement row on file (no consent record). Separate from Pro-Delayed — those Pros have an agreement but are on the 15-min delayed tier; these users don't have an agreement at all.` },
        { field: 'TOTAL', headerName: 'Total Unique Users', minWidth: 170, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right' },
          headerTooltip: `Pro + Non-Pro combined. Distinct users this source recorded in this specific month.` },
      ];
    }
    if (this.view_mode === 'monthly_counts') {
      const stat = this.monthly_count_column_label();
      // IT-F2-426 (Mike checklist 910a4a4c + clarification e265b763):
      // header tooltips explaining what each Counts by Month column is
      // actually counting. The wording flips with the Date field pick.
      // For "Active during window" (option A) the numbers are per-month
      // active-user headcount and CUMULATIVE is running peak, not a sum.
      // IT-F2-426 c/a8dcebce: login_periods now shares the active-in-
      // window headcount/peak semantics, so the "isActive" flag that
      // drives the tooltip + Peak-label variants covers both.
      const dfvHere = this.filter_date_field.value;
      const isActive = dfvHere === 'active_in_window' || dfvHere === 'login_periods';
      const eventLabel = isActive
        ? 'the active-during-window snapshot'
        : stat.toLowerCase().replace(/ count$/, '');
      const perMonthCountDesc = isActive
        ? `users active in this specific month (their (EARLIEST_DATE, DEACTIVATION_DATE) interval overlaps the month; blank deactivation = still active)`
        : `unique users whose ${eventLabel} date lands in this month`;
      const cumDesc = isActive
        ? `Running PEAK — highest Total headcount seen from the first month in view through this row. A snapshot isn't additive, so this shows the max simultaneous active users so far, not a running sum.`
        : `Running total of the Total column from the earliest month in the current view up through this row (inclusive). Reflects the customer + date-range filters above.`;
      // IT-F2-426 518a835e (Mike 2026-09-28 c/5b547985): numeric cells
      // become clickable → drill-down modal.
      const drillClick = (evt: any) => this.on_monthly_counts_cell_clicked(evt);
      const clickable = (base: any) => ({ ...base, onCellClicked: drillClick, cellStyle: { ...(base.cellStyle || {}), cursor: 'pointer', 'text-decoration': 'underline dotted' } });
      // No leading checkbox in monthly view — selection is per-user only.
      return [
        { field: 'MONTH', headerName: 'Month (YYYY-MM, ET)', minWidth: 170, sortable: true, filter: 'agTextColumnFilter',
          headerTooltip: isActive
            ? `Calendar month (ET-anchored, America/New_York) that the active-users headcount is measured over. Rendered as YYYY-MM.`
            : `Calendar month (ET-anchored, America/New_York) that the ${eventLabel} date fell into. Rendered as YYYY-MM.` },
        clickable({ field: 'PRO', headerName: `Pro ${stat}`, minWidth: 130, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right' },
          headerTooltip: `Click a cell to see the Pro users contributing. Count of Pro (professional) ${perMonthCountDesc}. Change the Date field filter above to switch between Active during window / First-Signed / Activation / Deactivation.` }),
        clickable({ field: 'NON_PRO', headerName: `Non-Pro ${stat}`, minWidth: 150, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right' },
          headerTooltip: `Click a cell to see the Non-Pro users contributing. Count of Non-Pro (nonprofessional) ${perMonthCountDesc}. Change the Date field filter above to switch between Active during window / First-Signed / Activation / Deactivation.` }),
        // IT-F2-426 c/cb897b64: Pro-Delayed column. Non-employee Pros
        // on 15-min delayed data. Excluded from NYSE §4.2.4/§4.2.5
        // reports (not reportable while delayed) but shown here so
        // operators can see the population.
        clickable({ field: 'PRO_DELAYED', headerName: `Pro-Delayed ${stat}`, minWidth: 160, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right', 'color': '#f59e0b' },
          headerTooltip: `Click a cell to see the Pro-Delayed users contributing. Non-employee Pros currently on 15-minute delayed data (Company Info → pro_15min_delayed=true, no cutoff or earliest date < cutoff). NOT reportable to NYSE while delayed; broken out here so operators can see the population. Employees always report as Pro regardless.` }),
        // IT-F2-426 b86d9456 (Mike 2026-09-25 c/0999f711): Delayed column
        // — users seen in a login source with no Exchange_Agreement,
        // scoped to the current customers + date-range filter.
        clickable({ field: 'DELAYED', headerName: `Delayed ${stat}`, minWidth: 150, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right', 'color': '#9ca3af', 'font-style': 'italic' },
          headerTooltip: `Click a cell to see the Delayed users contributing. Users seen in a login source (AuthLogins main + legacy + Graphem_Compliance + AuthLogins_Graphem) during this month with NO Exchange_Agreement on file. Deduped across sources per (email, customer). Separate from Pro-Delayed — those have an agreement but are on the 15-min delayed tier; these users don't have an agreement at all.` }),
        clickable({ field: 'TOTAL', headerName: `Total ${stat}`, minWidth: 140, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right' },
          headerTooltip: isActive
            ? `Click a cell to see every user contributing. Pro + Non-Pro + Pro-Delayed + Delayed active-user headcount for this specific month.`
            : `Click a cell to see every user contributing. Pro + Non-Pro + Pro-Delayed + Delayed for this month. Same dedup rule (unique per user_id + customer for the agreement-backed classes; unique per (email, customer) for Delayed).` }),
        { field: 'CUMULATIVE', headerName: isActive ? 'Peak' : 'Cumulative', minWidth: 140, sortable: true, filter: 'agNumberColumnFilter', cellStyle: { 'text-align': 'right' },
          headerTooltip: cumDesc },
      ];
    }
    if (this.view_mode === 'login_periods') {
      // Focused period-centric column set — one row per (user, period).
      // Backend already emits with EARLIEST_DATE=period.activation and
      // DEACTIVATION_DATE=period.deactivation when date_field='login_periods'.
      // MUST parse as UTC — periods are anchored at UTC midnight
      // (activation) or UTC 23:59:59 (deactivation). Parsing in local
      // zone shifts them by a day for west-coast viewers (Mike caught
      // 2026-09-01 c/8a5dafa3 with c:\temp\xxx.png showing 01/31/2024
      // when backend emits 2024-02-01T00Z).
      const fmt_date = (p: any) => {
        if (!p.value) return '';
        const dt = DateTime.fromISO(p.value, { zone: 'utc' });
        return dt.isValid ? dt.toFormat('MM/dd/yyyy') : '';
      };
      return [
        {
          headerCheckboxSelection: true,
          checkboxSelection: true,
          width: 44, maxWidth: 44, pinned: 'left',
          sortable: false, filter: false, resizable: false, suppressMovable: true,
        },
        { field: 'CUSTOMER', headerName: 'Customer', minWidth: 120, sortable: true, filter: 'agSetColumnFilter' },
        { field: 'SUBSCRIBERS_EMAIL_ADDRESS', headerName: 'Email', minWidth: 220, sortable: true, filter: 'agTextColumnFilter', cellStyle: { 'font-family': 'monospace', 'font-size': '11px' } },
        { field: 'SUBSCRIBERS_LAST_NAME', headerName: 'Last', minWidth: 120, sortable: true, filter: 'agTextColumnFilter' },
        { field: 'SUBSCRIBERS_FIRST_NAME', headerName: 'First', minWidth: 120, sortable: true, filter: 'agTextColumnFilter' },
        // IT-F2-426 c/cb897b64: PRO_STATUS can be 'Pro' / 'Non-Pro' / 'Pro-Delayed'.
        // Renderer highlights Pro-Delayed in amber so the row visually
        // separates from Non-Pro users below.
        { field: 'PRO_STATUS', headerName: 'Pro / Non-Pro', minWidth: 130, sortable: true, filter: 'agSetColumnFilter',
          cellStyle: (p: any) => p.value === 'Pro-Delayed' ? { color: '#f59e0b', 'font-weight': 'bold' } : {},
          headerTooltip: `Pro / Non-Pro classification. "Pro-Delayed" appears when the Company Info Pro-tier 15-min-delayed setting is on — these users are professional but on delayed data, so NOT reportable to NYSE while delayed. Employees always report as Pro regardless.` },
        { field: 'EARLIEST_DATE', headerName: 'Activation', minWidth: 140, sortable: true, filter: 'agDateColumnFilter', valueFormatter: fmt_date },
        { field: 'DEACTIVATION_DATE', headerName: 'Deactivation', minWidth: 140, sortable: true, filter: 'agDateColumnFilter', valueFormatter: fmt_date, cellStyle: (p: any) => ({ color: p.value ? '#fca5a5' : '#86efac' }) },
        { field: 'SCANNERS', headerName: 'Scanners', minWidth: 220, sortable: true, filter: 'agTextColumnFilter' },
        { field: 'SOURCES', headerName: 'Sources', minWidth: 260, sortable: true, filter: 'agTextColumnFilter', cellStyle: { 'font-size': '11px' }, tooltipField: 'SOURCES' },
        {
          field: 'LAST_LOGIN', headerName: 'Last Login', minWidth: 180, sortable: true, filter: 'agDateColumnFilter',
          valueFormatter: (p: any) => {
            if (!p.value) return '';
            const utc = DateTime.fromISO(p.value, { zone: 'utc' });
            return utc.isValid ? utc.toLocal().toFormat('MM/dd/yyyy HH:mm:ss') : '';
          },
        },
        { field: 'USER_ID', headerName: 'F2 User ID', minWidth: 260, sortable: true, filter: 'agTextColumnFilter', cellStyle: { 'font-family': 'monospace', 'font-size': '11px' } },
      ];
    }
    return this.grid.columnDefs;
  }

  // Copy the currently-visible monthly counts as TSV to clipboard for
  // Excel paste. Same source-selection logic as export_monthly_counts_csv.
  async copy_monthly_counts_to_excel() {
    const source = (this.view_mode === 'monthly_counts' && this.grid?.api)
      ? this._get_filtered_rows()
      : this.monthly_counts_rows;
    if (!source.length) {
      this.toast.info('Nothing to copy', 'No first-signed rows in the current filter.');
      return;
    }
    const stat = this.monthly_count_column_label();
    const headers = ['Month', `Pro ${stat}`, `Non-Pro ${stat}`, `Pro-Delayed ${stat}`, `Delayed ${stat}`, `Total ${stat}`, 'Cumulative'];
    const lines = [headers.join('\t')];
    for (const r of source) {
      lines.push([r.MONTH, r.PRO, r.NON_PRO, r.PRO_DELAYED || 0, r.DELAYED || 0, r.TOTAL, r.CUMULATIVE].join('\t'));
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      this.toast.success('Copied', `${source.length} monthly rows on clipboard`);
    }
    catch (e: any) {
      this.toast.error('Copy failed', e?.message || 'clipboard denied');
    }
  }

  // Export the currently-visible monthly counts as CSV. Simple RFC 4180
  // comma-CSV of the three columns. In 'monthly_counts' view, source is
  // the grid-filtered monthly grid — so ag-grid filters on MONTH/COUNT/
  // CUMULATIVE narrow the export. In 'users' view, monthly_counts_rows
  // is already derived from the grid-filtered users grid (via
  // _rebuild_monthly_counts). Either way, ag-grid state is honored.
  /** IT-F2-426 (Mike 2026-09-24 checklist bf7a267f): unified export filename
   *  builder. Mike's spec:
   *
   *    {yyyy-MM-dd HH:mm}-{report}-{From yyyy-MM}[ to {yyyy-MM}].{ext}
   *
   *  Windows disallows ':' in filenames, so we render HH-mm instead of HH:mm.
   *  The range half is:
   *    - single month (From == To OR To blank)  → "-2026-08"
   *    - range (From != To)                     → "-2026-06 to 2026-08"
   *    - both blank                             → "-all-time"
   *    - open-ended From only                   → "-up to {yyyy-MM}"
   *    - open-ended To only                     → "-from {yyyy-MM}"
   *  Called by every export button on this component so the naming is one
   *  source of truth. */
  private _export_filename(report: string, ext: string): string {
    const stamp = DateTime.now().toFormat('yyyy-MM-dd HH-mm');
    const fromYm = String(this.filter_month_from.value || '').trim();
    const toYm = String(this.filter_month_to.value || '').trim();
    let rangePart: string;
    if (fromYm && toYm) {
      rangePart = (fromYm === toYm) ? `-${fromYm}` : `-${fromYm} to ${toYm}`;
    } else if (fromYm) {
      rangePart = `-from ${fromYm}`;
    } else if (toYm) {
      rangePart = `-up to ${toYm}`;
    } else {
      rangePart = `-all-time`;
    }
    return `${stamp}-${report}${rangePart}.${ext}`;
  }

  export_monthly_counts_csv() {
    const source = (this.view_mode === 'monthly_counts' && this.grid?.api)
      ? this._get_filtered_rows()
      : this.monthly_counts_rows;
    if (!source.length) {
      this.toast.info('Nothing to export', 'No first-signed rows in the current filter.');
      return;
    }
    const lines: string[] = ['MONTH,PRO,NON_PRO,PRO_DELAYED,DELAYED,TOTAL,CUMULATIVE'];
    for (const r of source) {
      lines.push(`${r.MONTH},${r.PRO},${r.NON_PRO},${r.PRO_DELAYED || 0},${r.DELAYED || 0},${r.TOTAL},${r.CUMULATIVE}`);
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const filename = this._export_filename('counts-by-month', 'csv');
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    this.toast.success('Exported', `${filename} (${source.length} months)`);
  }

  // Mirrors the current filter state (both top-bar filters AND ag-grid
  // column filters) into the URL query string using `replaceUrl: true` —
  // no new browser-history entries, so back/forward stays clean. Enables
  // share-via-URL and refresh-preserves-filters. ag-grid model is
  // serialized as JSON in ?gf= (small enough for typical use, degrades
  // gracefully if the URL blows past browser limits).
  private _sync_url_from_filters() {
    const cust = this.filter_customers.value || [];
    let gf: string | null = null;
    if (this.grid?.api) {
      const model = this.grid.api.getFilterModel();
      if (model && Object.keys(model).length > 0) {
        gf = encodeURIComponent(JSON.stringify(model));
      }
    }
    const df_field = this.filter_date_field.value;
    const queryParams: any = {
      customers: cust.length ? cust.join(',') : null,
      date_from: this.filter_date_from.value || null,
      date_to: this.filter_date_to.value || null,
      date_field: df_field && df_field !== 'active_in_window' ? df_field : null,
      // IT-F2-426 c845fcec (Mike 2026-09-25 c/7d335dbf): view_mode picker
      // in the URL so a refresh keeps the tab. 'users' is the default so
      // omit it from the URL for the common case.
      tab: this.view_mode && this.view_mode !== 'users' ? this.view_mode : null,
      gf,
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  async refresh() {
    await this.load_report();
  }

  clear_filters() {
    this.filter_customers.reset([]);
    this.filter_month_from.reset('');
    this.filter_month_to.reset('');
    this.filter_date_from.reset('');
    this.filter_date_to.reset('');
    this.filter_date_field.reset('active_in_window');
    if (this.grid?.api) this.grid.api.setFilterModel(null);
    this.load_report();
  }

  // Month-quick-pick: sets date_from/date_to to the first/last day of the
  // month offset from today. Backend interprets this as "active during
  // window" — rows kept when scanner tracking overlaps the range.
  // offset: 0 = this month, -1 = last month, -2 = two months ago, etc.
  pick_month(offset: number) {
    const target = DateTime.now().plus({ months: offset });
    const ym = target.toFormat('yyyy-MM');
    // Quick-picks map to a single-month range (from = to).
    this.filter_month_from.setValue(ym);
    this.filter_month_to.setValue(ym);
  }

  // Copy the current grid rows to clipboard as TSV so Excel paste-formats
  // into rows/columns. Headers included as row 1. Respects post-filter
  // ordering / visibility that the operator has applied in the grid.
  // INCLUDES display-only columns (Pro/Non-Pro) alongside the 36 Exhibit B
  // cols — this is internal review, not the exchange submission.
  // Format a date field for export. UTC-midnight ISO strings (period
  // boundaries, Graphem backfills, Excel-serial imports) become
  // "MM/DD/YYYY" (the UTC calendar date — unambiguous). Real timestamps
  // become "MM/DD/YYYY HH:mm:ss" in local time. Anything else passes
  // through unchanged. Mike 2026-09-01 c/5e8d1cfb: raw ISO in Excel was
  // getting misread as prior-day-in-local-tz.
  private _fmt_export_date_field(value: any): string {
    const s = String(value ?? '');
    if (!s) return '';
    if (!/^\d{4}-\d{2}-\d{2}T/.test(s)) return s;
    const utc = DateTime.fromISO(s, { zone: 'utc' });
    if (!utc.isValid) return s;
    // Backend anchors periods at UTC midnight (activation) or UTC 23:59:59
    // (deactivation = last-day-of-month). Both are calendar-date anchors.
    const is_date_anchor =
      (utc.hour === 0 && utc.minute === 0 && utc.second === 0) ||
      (utc.hour === 23 && utc.minute === 59 && utc.second === 59);
    return is_date_anchor ? utc.toFormat('MM/dd/yyyy') : utc.toLocal().toFormat('MM/dd/yyyy HH:mm:ss');
  }

  private _is_display_date_field(key: string): boolean {
    return key === 'EARLIEST_DATE' || key === 'DEACTIVATION_DATE' || key === 'LAST_LOGIN';
  }

  async copy_to_excel() {
    const visible_rows = this._get_filtered_rows();
    if (visible_rows.length === 0) {
      this.toast.error('Nothing to copy', '0 users qualify with the current filters/selection.');
      return;
    }
    const all_cols = [...EXHIBIT_B_COLS, ...DISPLAY_ONLY_COLS];
    const lines: string[] = [];
    lines.push(all_cols.map(c => c.header).join('\t'));
    for (const r of visible_rows) {
      lines.push(all_cols.map(c => {
        const v = (r as any)[c.key];
        return this._is_display_date_field(c.key) ? this._fmt_export_date_field(v) : String(v ?? '');
      }).join('\t'));
    }
    const tsv = lines.join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      this.toast.success('Copied to clipboard', `${visible_rows.length} rows + header. Paste into Excel.`);
    }
    catch (e: any) {
      this.toast.error('Copy failed', e?.message || 'clipboard write refused');
    }
  }

  // Comma-delimited CSV of the current grid state — includes ALL
  // visible columns (grid display order), respects grid filters + sorts.
  // For internal / operator use — NOT the exchange submission format.
  // Mike ask 2026-09-01: "export all is for us not the exchange so it
  // should be actually comma delimited using all visible columns."
  export_all_visible_csv() {
    const visible_rows = this._get_filtered_rows();
    if (visible_rows.length === 0) {
      this.toast.error('Nothing to export', '0 users qualify with the current filters/selection.');
      return;
    }
    // Grab displayed columns from grid state — respects any operator
    // column-reorder / hide via the column menu.
    const cols: { field: string; header: string }[] = [];
    if (this.grid?.api) {
      const displayed = this.grid.api.getAllDisplayedColumns?.() || [];
      for (const c of displayed) {
        const def = c.getColDef?.() || {};
        const field = def.field;
        if (!field) continue;
        cols.push({ field, header: def.headerName || field });
      }
    }
    // Fallback: all keys if grid isn't ready
    if (cols.length === 0 && visible_rows.length > 0) {
      for (const k of Object.keys(visible_rows[0])) cols.push({ field: k, header: k });
    }

    // CSV-quote: wrap in "..." if value contains comma, quote, or newline;
    // double internal quotes per RFC 4180.
    const csv_quote = (v: any): string => {
      const s = String(v ?? '');
      if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const lines: string[] = [];
    lines.push(cols.map(c => csv_quote(c.header)).join(','));
    for (const r of visible_rows) {
      lines.push(cols.map(c => {
        const v = (r as any)[c.field];
        return csv_quote(this._is_display_date_field(c.field) ? this._fmt_export_date_field(v) : v);
      }).join(','));
    }
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const cust = this.filter_customers.value || [];
    const cust_slug = cust.length === 0 ? 'all' : cust.length === 1 ? cust[0] : 'multi';
    const filename = this._export_filename(`compliance-report_${cust_slug}`, 'csv');
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    this.toast.success('Exported', `${filename} (${visible_rows.length} rows, ${cols.length} cols)`);
  }

  // Pipe-delimited CSV per NYSE Exhibit B §4.2.5. Filename shape
  // exhibit-b_<customer|all>_YYYY-MM-DD.csv. When multiple customers
  // are selected filename says "multi". Optional pro_filter narrows
  // to just Pro or Non-Pro rows for the two split-submission buttons —
  // exchanges typically want Professional and Non-Professional as
  // separate files. STRICT 36-col NYSE Exhibit B format only.
  async export_csv(pro_filter?: 'Pro' | 'Non-Pro') {
    // Fetch login-period rows so activation/deactivation reflect actual
    // usage. Then dedupe to ONE row per user (min activation, max
    // deactivation, blank if still active) — Exhibit B should not
    // repeat users unless there are repeat agreements. Mike 2026-09-01
    // c/74ea0391.
    const period_rows_all = await this._fetch_login_period_rows();
    const period_rows_selected = this._apply_selection_to_login_period_rows(period_rows_all);
    const one_per_user = this._dedupe_period_rows_to_one_per_user(period_rows_selected);
    const visible_rows = pro_filter
      ? one_per_user.filter(r => r.PRO_STATUS === pro_filter)
      : one_per_user;
    if (visible_rows.length === 0) {
      this.toast.error('Nothing to export', `0 users qualify for the ${pro_filter || 'exhibit'} report with the current filters/selection.`);
      return;
    }
    const lines: string[] = [];
    lines.push(EXHIBIT_B_COLS.map(c => c.header).join('|'));
    for (const r of visible_rows) {
      // Fields with a literal | in them get stripped — pipe is the delimiter
      // and the spec has no quoting convention for it. Belt-and-braces
      // sanitize newlines too.
      lines.push(EXHIBIT_B_COLS.map(c => String((r as any)[c.key] ?? '').replace(/[|\r\n]/g, ' ')).join('|'));
    }
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const cust = this.filter_customers.value || [];
    const cust_slug = cust.length === 0 ? 'all' : cust.length === 1 ? cust[0] : 'multi';
    const pro_slug = pro_filter === 'Pro' ? 'pro_' : pro_filter === 'Non-Pro' ? 'nonpro_' : '';
    // .xls extension so Excel opens with the import wizard (user picks
    // pipe delim). Prevents the auto-parse-as-csv mangling that comma-
    // containing subscriber fields hit. Mike ask 2026-09-02.
    const filename = this._export_filename(`nonpro-exhibit-b_${pro_slug}${cust_slug}`.replace(/^_+|_+$/g, ''), 'xls');
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    this.toast.success('Exported', `${filename} (${visible_rows.length} rows)`);
  }

  // Fetches a fresh report with date_field=login_periods regardless of
  // the current view / picker. Used by the exhibit CSV exports so that
  // ACTIVATION_DATE / DEACTIVATION_DATE come from the login-based
  // period computation (Graphem_Compliance + AuthLogins main+legacy),
  // not the local Exchange_Agreements rollup. Mike ask 2026-09-01
  // c/3beb9a3e: "exhibit reports activations/deactivations isn't coming
  // from the logins we worked on".
  private async _fetch_login_period_rows(): Promise<any[]> {
    const query: any = { date_field: 'login_periods' };
    const cust = this.filter_customers.value;
    if (cust && cust.length > 0) query.customers = cust;
    if (this.filter_date_from.value) query.date_from = this.filter_date_from.value;
    if (this.filter_date_to.value) query.date_to = this.filter_date_to.value;
    const res: any = await this.adminService.getExhibitBReport(query);
    return (res && !res.err && Array.isArray(res.rows)) ? res.rows : [];
  }

  // IT-F2-367 ER-D526HTV — Employee entitlement report also needs to
  // include Pro users with zero login history (e.g. F2 internal admins
  // whose agreement was created directly on the admin side and haven't
  // gone through the tracker login funnel). This helper fetches from
  // active_in_window mode which reads Exchange_Agreements directly, so
  // synthetic/admin-created rows appear. Called as a fallback merge with
  // _fetch_login_period_rows() results for the Employee export path.
  private async _fetch_active_in_window_rows(): Promise<any[]> {
    const query: any = { date_field: 'active_in_window' };
    const cust = this.filter_customers.value;
    if (cust && cust.length > 0) query.customers = cust;
    if (this.filter_date_from.value) query.date_from = this.filter_date_from.value;
    if (this.filter_date_to.value) query.date_to = this.filter_date_to.value;
    const res: any = await this.adminService.getExhibitBReport(query);
    return (res && !res.err && Array.isArray(res.rows)) ? res.rows : [];
  }

  // Merge login-period-derived rows with agreement-only rows: any user
  // in active_in_window who isn't already in login_periods gets appended
  // with EARLIEST_DATE as activation (from agreement) and blank
  // DEACTIVATION_DATE. IT-F2-367 ER-D526HTV fix — Mike's synthetic F2
  // admin row had no login history so was invisible to the login-period
  // pipeline. Preserves the dedupe key (USER_ID_INTERNAL|CUSTOMER) so
  // no duplicates.
  private async _merge_agreement_only_users(period_rows: any[]): Promise<any[]> {
    const active = await this._fetch_active_in_window_rows();
    const seen = new Set(period_rows.map(r => `${r.USER_ID_INTERNAL || r.USER_ID}|${r.CUSTOMER}`));
    const extras = active.filter(r => !seen.has(`${r.USER_ID_INTERNAL || r.USER_ID}|${r.CUSTOMER}`));
    return period_rows.concat(extras);
  }

  // Collapses login-period rows (one per period) to ONE row per user.
  // Uses MIN activation across the user's periods, and MAX deactivation
  // — but if any period is currently active (deactivation blank),
  // deactivation stays blank on the merged row. Mike ask 2026-09-01
  // c/74ea0391: "exhibit b non pro report shouldn't repeat users
  // unless there's repeat agreements". Applies to both exhibit
  // exports (36-col + 7-col entitlement).
  private _dedupe_period_rows_to_one_per_user(rows: any[]): any[] {
    // Cutoff for the "clamp deactivation to null when it's beyond the
    // filter's date_to" rule. Mike ask 2026-09-02: entitlement report
    // filter shouldn't show deactivate dates in the future.
    const cutoff_iso = this.filter_date_to.value
      ? `${this.filter_date_to.value}T23:59:59.999Z`
      : '';
    const by_user = new Map<string, any>();
    for (const r of rows) {
      const key = `${r.USER_ID_INTERNAL || r.USER_ID}|${r.CUSTOMER}`;
      const existing = by_user.get(key);
      if (!existing) {
        by_user.set(key, { ...r });
        continue;
      }
      // MIN activation
      if (r.EARLIEST_DATE && (!existing.EARLIEST_DATE || r.EARLIEST_DATE < existing.EARLIEST_DATE)) {
        existing.EARLIEST_DATE = r.EARLIEST_DATE;
      }
      // Deactivation: blank wins (user still active on any period); else MAX
      if (!r.DEACTIVATION_DATE || !existing.DEACTIVATION_DATE) {
        existing.DEACTIVATION_DATE = null;
      }
      else if (r.DEACTIVATION_DATE > existing.DEACTIVATION_DATE) {
        existing.DEACTIVATION_DATE = r.DEACTIVATION_DATE;
      }
      // Union sources across periods for transparency
      if (r.SOURCES) {
        const cur = new Set((existing.SOURCES || '').split(', ').filter(Boolean));
        for (const s of r.SOURCES.split(', ')) if (s) cur.add(s);
        existing.SOURCES = [...cur].sort().join(', ');
      }
    }
    // Second pass: clamp deactivation to null when it exceeds the cutoff.
    if (cutoff_iso) {
      for (const row of by_user.values()) {
        if (row.DEACTIVATION_DATE && row.DEACTIVATION_DATE > cutoff_iso) {
          row.DEACTIVATION_DATE = null;
        }
      }
    }
    return [...by_user.values()];
  }

  // Narrows the login-period rows to the user's current row selection
  // (if any) — matches on USER_ID_INTERNAL so we don't lose period
  // rows for selected users. Falls back to all login-period rows when
  // nothing is selected on the grid.
  private _apply_selection_to_login_period_rows(period_rows: any[]): any[] {
    const selected = this.grid?.api?.getSelectedRows?.() || [];
    if (selected.length === 0) return period_rows;
    const sel_keys = new Set(selected.map((r: any) => `${r.USER_ID_INTERNAL || r.USER_ID}|${r.CUSTOMER}`));
    return period_rows.filter(r => sel_keys.has(`${r.USER_ID_INTERNAL || r.USER_ID}|${r.CUSTOMER}`));
  }

  // BOTH exhibit CSV exports are for NONPROFESSIONAL users — there's no
  // Pro report currently (Mike 2026-09-01 c/54e0785d: "both are non pro
  // reports. we currently don't have a pro report"). Two Non-Pro formats:
  //   - §4.2.4 Nonprofessional Exhibit B: 36-col detailed subscriber
  //     record (name, address, employer, Q1-Q11). Emitted by
  //     export_csv_nonpro_exhibit.
  //   - Nonprofessional Entitlement Record: 7-col compact entitlement
  //     format (USER_ID | SUBSCRIBER_NAME | QUANTITY | ACTIVATION_DATE
  //     | DEACTIVATION_DATE | TYPE_OF_DATA | TYPE_OF_PRODUCT). 2 rows
  //     per user for CTA Network A + B (codes 35019 / 43019). Emitted
  //     by export_csv_nonpro_entitlement.
  async export_csv_nonpro_entitlement() {
    // Fetch + zero-check BEFORE prompting for codes — no point asking
    // the operator to fill in QUANTITY/codes/type if there are no
    // qualifying users. Mike ask 2026-09-01 c/9c6e6446.
    // Also dedup to one row per user (Mike c/74ea0391) — each user gets
    // 2 rows in the export (Network A + B) not 2 × periods.
    const period_rows_all = await this._fetch_login_period_rows();
    const period_rows_selected = this._apply_selection_to_login_period_rows(period_rows_all);
    const one_per_user = this._dedupe_period_rows_to_one_per_user(period_rows_selected);
    const rows = one_per_user.filter(r => r.PRO_STATUS === 'Non-Pro');
    if (rows.length === 0) {
      this.toast.error('Nothing to export', '0 Non-Pro users qualify for the entitlement report with the current filters/selection.');
      return;
    }
    const qty = window.prompt('QUANTITY (# of entitlements per user, typically 1)', '1') || '1';
    const code_a = window.prompt('CTA Network A Product Code (Non-Pro)', '35019') || '35019';
    const code_b = window.prompt('CTA Network B Product Code (Non-Pro)', '43019') || '43019';
    const type_of_product = window.prompt('TYPE_OF_PRODUCT (Device, Data Feed, API, etc.)', 'Device') || 'Device';
    const fmt_ymd = (iso: string): string => {
      if (!iso) return '';
      const d = DateTime.fromISO(iso, { zone: 'utc' });
      return d.isValid ? d.toFormat('yyyyMMdd') : '';
    };
    const strip_pipe = (s: any): string => String(s ?? '').replace(/[|\r\n]/g, ' ');
    const headers = ['USER_ID', 'SUBSCRIBER_NAME', 'QUANTITY', 'ACTIVATION_DATE', 'DEACTIVATION_DATE', 'TYPE_OF_DATA', 'TYPE_OF_PRODUCT'];
    const lines: string[] = [headers.join('|')];
    for (const r of rows) {
      const first = r['SUBSCRIBERS_FIRST_NAME'] || '';
      const last = r['SUBSCRIBERS_LAST_NAME'] || '';
      const full_name = strip_pipe(`${first} ${last}`.trim());
      const uid = strip_pipe(r['USER_ID']);
      const act = fmt_ymd(r['EARLIEST_DATE'] || '');
      const deact = fmt_ymd(r['DEACTIVATION_DATE'] || '');
      for (const code of [code_a, code_b]) {
        lines.push([
          uid, full_name, strip_pipe(qty), act, deact,
          strip_pipe(code), strip_pipe(type_of_product),
        ].join('|'));
      }
    }
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const cust = this.filter_customers.value || [];
    const cust_slug = cust.length === 0 ? 'all' : cust.length === 1 ? cust[0] : 'multi';
    // .xls extension — Excel opens with import wizard so operator can
    // pick pipe delim, avoiding the auto-CSV-parse mangling of comma-
    // containing subscriber fields. Mike ask 2026-09-02.
    const filename = this._export_filename(`nonpro-entitlement_${cust_slug}`.replace(/_+$/, ''), 'xls');
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    this.toast.success('Exported', `${filename} (${rows.length} users × 2 codes = ${rows.length * 2} rows)`);
  }

  async export_csv_nonpro_exhibit() { await this.export_csv('Non-Pro'); }

  // F2-employee variant of export_csv_nonpro_exhibit — same 36-col §4.2.5
  // Exhibit B shape, filtered to F2 internal admin/professional users only
  // (customer==='f2' + PRO_STATUS==='Pro'). Mike 2026-09-09 IT-F2-367:
  // "these are admin users we are reporting" — the "Pro report" is really
  // an F2-employee report. Non-F2 pro users (e.g. Simpler Trading's Mirza)
  // belong on their own customer's compliance reports, not F2's.
  async export_csv_employee_exhibit() {
    await this.export_csv_filtered_rows(
      (r: any) => r.CUSTOMER === 'f2' && r.PRO_STATUS === 'Pro',
      'employee',
    );
  }

  // Shared helper — same shape as export_csv() but accepts a custom filter
  // predicate and filename slug instead of the Pro/Non-Pro string filter.
  // Merges login_periods (real activation/deactivation from usage) with
  // active_in_window (agreement-direct, includes admin-created rows with
  // no login history) — login_periods wins when present. IT-F2-367
  // ER-D526HTV: F2 admin Mike's agreement had zero login history so
  // period-only fetch returned no rows.
  private async export_csv_filtered_rows(pred: (r: any) => boolean, slug: string) {
    const period_rows_all = await this._fetch_login_period_rows();
    const period_rows_selected = this._apply_selection_to_login_period_rows(period_rows_all);
    const one_per_user = this._dedupe_period_rows_to_one_per_user(period_rows_selected);
    const merged = await this._merge_agreement_only_users(one_per_user);
    const visible_rows = merged.filter(pred);
    if (visible_rows.length === 0) {
      this.toast.error('Nothing to export', `0 users qualify for the ${slug} report with the current filters/selection.`);
      return;
    }
    const lines: string[] = [];
    lines.push(EXHIBIT_B_COLS.map(c => c.header).join('|'));
    for (const r of visible_rows) {
      lines.push(EXHIBIT_B_COLS.map(c => String((r as any)[c.key] ?? '').replace(/[|\r\n]/g, ' ')).join('|'));
    }
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const cust = this.filter_customers.value || [];
    const cust_slug = cust.length === 0 ? 'all' : cust.length === 1 ? cust[0] : 'multi';
    const filename = this._export_filename(`employee-exhibit-b_${slug}_${cust_slug}`.replace(/_+$/, ''), 'xls');
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    this.toast.success('Exported', `${filename} (${visible_rows.length} rows)`);
  }

  // Employee Entitlement Record per NYSE Comprehensive Market Data Policies
  // §9.2 (Professional Users Entitlement Records) — 17-col pipe-delimited:
  //   VAN | VAN_NAME | USER_ID | SUBSCRIBER NAME | LEGAL COMPANY NAME |
  //   INSTALLATION ADDRESS 1/2/3 | CITY | STATE | COUNTRY | ZIP CODE |
  //   QUANTITY | ACTIVATION_DATE | DEACTIVATION_DATE | TYPE_OF_DATA | PRODUCT
  //
  // Filters to F2 internal admin/professional users (customer==='f2' +
  // PRO_STATUS==='Pro'). Two rows per user (CTA Network A + B codes).
  //
  // Firm-level fields (VAN, VAN_NAME, legal name, installation address,
  // TYPE_OF_PRODUCT) sourced from Customer.f2 metadata via
  // GET /admin/company-info. Edit via "Edit Company Info" button on the
  // toolbar — no more per-run prompts (IT-F2-367 Mike 2026-09-09).
  //
  // Only per-report prompts: QUANTITY + CTA Network A/B product codes
  // (Pro codes are firm-specific and shouldn't be baked into defaults).
  //
  // Per-user installation address pulled from agreement subscriber block
  // (Jim spec cid b0bb65e2: "Installation address fields = location
  // where the entitlement is used"). Falls back to firm data-center
  // location from company_info when a user row is blank.
  //
  // NYSE spec: https://www.nyse.com/publicdocs/nyse/data/NYSE_Proprietary_Market_Data_Comprehensive_Policy_Package.pdf
  async export_csv_employee_entitlement() {
    // Ensure company_info is loaded — silent re-fetch if empty (component
    // init may not have finished by first click).
    if (!this.company_info?.nyse_vendor_account_number) {
      await this._load_company_info();
    }
    const period_rows_all = await this._fetch_login_period_rows();
    const period_rows_selected = this._apply_selection_to_login_period_rows(period_rows_all);
    const one_per_user = this._dedupe_period_rows_to_one_per_user(period_rows_selected);
    const merged = await this._merge_agreement_only_users(one_per_user);
    // F2-employee filter (Mike 2026-09-09): customer must be 'f2' AND
    // PRO_STATUS must be 'Pro'. Excludes external Pro users like
    // Simpler Trading's Mirza (who belong on Simpler's reports).
    const rows = merged.filter(r => r.CUSTOMER === 'f2' && r.PRO_STATUS === 'Pro');
    if (rows.length === 0) {
      this.toast.error('Nothing to export', '0 F2 employees qualify for the Employee entitlement report with the current filters/selection. (Filter is customer===\'f2\' + PRO_STATUS===\'Pro\'.)');
      return;
    }
    // Firm-level fields from Customer.f2 metadata (edit via button).
    const van = this.company_info?.nyse_vendor_account_number || '';
    const van_name = this.company_info?.data_center_location?.name || '';
    const legal_company = this.company_info?.legal_name || '';
    // IT-F2-367 Jim 2026-09-11 13:32Z reversal: INSTALLATION ADDRESS is the
    // DATA CENTER address (phoenixNAP), NOT the firm registered office.
    // Sourced from Customer.f2.data_center_location.{address,city,state,zip_code}.
    // (Prior state: firm registered address per Jim 12:24Z — now reversed.)
    const firm_addr_1 = this.company_info?.data_center_location?.address || '';
    const firm_city = this.company_info?.data_center_location?.city || '';
    const firm_state = this.company_info?.data_center_location?.state || '';
    const firm_zip = this.company_info?.data_center_location?.zip_code || '';
    const firm_country = this.company_info?.data_center_location?.country || this.company_info?.country || 'US';
    const product = this.company_info?.default_type_of_product || 'Device';
    if (!van || !legal_company) {
      this.toast.error('Company info missing', 'VAN and LEGAL COMPANY NAME are required. Click "Edit Company Info" and fill in the firm details.');
      return;
    }
    // IT-F2-367 (Jim 2026-09-11 12:48Z reversed Q7=C from 15 → 17 cols):
    // restored NYSE §9.2 Professional Users Entitlement Record 17-col spec.
    // If Customer.f2 has pro-tier CTA codes stored → one row per (user × pro
    // code) with TYPE_OF_DATA/PRODUCT populated. If none stored → emit ONE
    // row per employee with TYPE_OF_DATA/PRODUCT blank (operator fills at
    // desk) — Jim ER-P7AW2P7 2026-09-11 13:18Z: "why isn't mike showing up"
    // — hard-blocking on missing codes hid employees entirely.
    // Accept entries with either a numeric code (custom table) OR a
    // label from the checkbox picker (Jim 2026-09-11 13:55Z — no more
    // required numeric-code lookup). Export column emits code || label.
    const pro_codes = (this.company_info?.nyse_cta_codes || []).filter((c: any) => c.tier === 'pro' && (c.code || c.label));
    const qty = window.prompt('QUANTITY (# of entitlements per user, typically 1)', '1') || '1';
    const fmt_ymd = (iso: string): string => {
      if (!iso) return '';
      const d = DateTime.fromISO(iso, { zone: 'utc' });
      return d.isValid ? d.toFormat('yyyyMMdd') : '';
    };
    const strip_pipe = (s: any): string => String(s ?? '').replace(/[|\r\n]/g, ' ');
    const headers = [
      'VAN', 'VAN_NAME', 'USER_ID', 'SUBSCRIBER NAME', 'LEGAL COMPANY NAME',
      'INSTALLATION ADDRESS 1', 'INSTALLATION ADDRESS 2', 'INSTALLATION ADDRESS 3',
      'CITY', 'STATE', 'COUNTRY', 'ZIP CODE',
      'QUANTITY', 'ACTIVATION_DATE', 'DEACTIVATION_DATE', 'TYPE_OF_DATA', 'PRODUCT',
    ];
    const lines: string[] = [headers.join('|')];
    for (const r of rows) {
      const first = r['SUBSCRIBERS_FIRST_NAME'] || '';
      const last = r['SUBSCRIBERS_LAST_NAME'] || '';
      const full_name = strip_pipe(`${first} ${last}`.trim());
      const uid = strip_pipe(r['USER_ID']);
      const act = fmt_ymd(r['EARLIEST_DATE'] || '');
      const deact = fmt_ymd(r['DEACTIVATION_DATE'] || '');
      // INSTALLATION ADDRESS = F2 firm registered address (all employee
      // rows use the same firm address per Jim 2026-09-11 12:24Z; not
      // the employee's home address).
      const install_addr_1 = firm_addr_1;
      const install_addr_2 = '';
      const install_addr_3 = '';
      const install_city = firm_city;
      const install_state = firm_state;
      const install_country = firm_country;
      const install_zip = firm_zip;
      // 17-col §9.2: one row per (user × pro CTA code) when codes stored,
      // else one row per user with blank TYPE_OF_DATA + PRODUCT (fill-at-desk).
      const code_iter = pro_codes.length > 0
        ? pro_codes.map((c: any) => ({ code: c.code || c.label, product: c.product || c.label || product }))
        : [{ code: '', product: '' }];
      for (const c of code_iter) {
        lines.push([
          strip_pipe(van), strip_pipe(van_name), uid, full_name, strip_pipe(legal_company),
          strip_pipe(install_addr_1), strip_pipe(install_addr_2), strip_pipe(install_addr_3),
          strip_pipe(install_city), strip_pipe(install_state), strip_pipe(install_country), strip_pipe(install_zip),
          strip_pipe(qty), act, deact, strip_pipe(c.code), strip_pipe(c.product),
        ].join('|'));
      }
    }
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const cust = this.filter_customers.value || [];
    const cust_slug = cust.length === 0 ? 'all' : cust.length === 1 ? cust[0] : 'multi';
    const filename = this._export_filename(`employee-entitlement_${cust_slug}`.replace(/_+$/, ''), 'xls');
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    const codes_note = pro_codes.length > 0 ? `× ${pro_codes.length} pro CTA codes` : `× 1 (TYPE_OF_DATA blank — fill at desk; add codes via Edit Company Info to auto-populate)`;
    this.toast.success('Exported', `${filename} (${rows.length} employees ${codes_note}, 17 cols per NYSE §9.2)`);
  }

  // Returns the rows exports should use: if any rows are selected via
  // the leading checkbox column, ONLY those selected rows (regardless
  // of ag-grid filter/sort state). Otherwise falls back to the
  // filter/sort-aware full set. Mike ask 2026-09-01 c/a0b9b549.
  private _get_filtered_rows(): any[] {
    if (!this.grid?.api) return this.rows;
    const selected = this.grid.api.getSelectedRows?.() || [];
    if (selected.length > 0) return selected;
    const out: any[] = [];
    this.grid.api.forEachNodeAfterFilterAndSort((node: any) => out.push(node.data));
    return out;
  }

  // Copies a compact JSON snapshot of the current report state to
  // clipboard for debugging — filter values, view mode, row counts,
  // sample rows. Mike ask 2026-09-01 c/f4100ad1: "I'd like a debug
  // copy button so I don't have to give you a screenshot to digest".
  async copy_debug() {
    const filtered = (this.grid?.api && this.view_mode !== 'monthly_counts')
      ? this._get_filtered_rows()
      : [];
    const selected = this.grid?.api?.getSelectedRows?.() || [];
    const grid_filter_model = this.grid?.api?.getFilterModel?.() || {};
    const pro_status_breakdown: Record<string, number> = {};
    for (const r of filtered) {
      const s = r?.PRO_STATUS || '(blank)';
      pro_status_breakdown[s] = (pro_status_breakdown[s] || 0) + 1;
    }
    // IT-F2-426 6e3a0ac6 (Mike 2026-09-28): label the snapshot so operators
    // know which report / view / window the debug output describes.
    const viewLabel =
      this.view_mode === 'monthly_counts'  ? 'Counts by Month' :
      this.view_mode === 'login_periods'   ? 'Login Periods' :
      this.view_mode === 'source_coverage' ? 'Source Coverage' :
      this.view_mode === 'report_page'     ? 'Report' :
      this.view_mode === 'employees'       ? 'Employees' :
      'Users';
    const windowPart =
      (this.filter_month_from.value && this.filter_month_to.value)
        ? (this.filter_month_from.value === this.filter_month_to.value
            ? this.filter_month_from.value
            : `${this.filter_month_from.value} → ${this.filter_month_to.value}`)
        : (this.filter_date_from.value || this.filter_date_to.value)
          ? `${this.filter_date_from.value || 'any'} → ${this.filter_date_to.value || 'any'}`
          : 'all time';
    const snapshot: any = {
      timestamp: new Date().toISOString(),
      label: `Compliance Report — ${viewLabel} — ${windowPart} — date_field=${this.filter_date_field.value}`,
      url: window.location.href,
      view_mode: this.view_mode,
      filters: {
        customers: this.filter_customers.value,
        date_field: this.filter_date_field.value,
        month_from: this.filter_month_from.value,
        month_to: this.filter_month_to.value,
        date_from: this.filter_date_from.value,
        date_to: this.filter_date_to.value,
      },
      grid_column_filters: grid_filter_model,
      counts: {
        total_backend_rows: this.rows.length,
        after_grid_filters: filtered.length,
        selected: selected.length,
        pro_status_breakdown,
        monthly_rows: this.monthly_counts_rows.length,
        monthly_source_rows: this.monthly_source_rows.length,
      },
      sample_first_5_after_grid_filter: filtered.slice(0, 5).map(r => ({
        USER_ID: r.USER_ID,
        CUSTOMER: r.CUSTOMER,
        PRO_STATUS: r.PRO_STATUS,
        EARLIEST_DATE: r.EARLIEST_DATE,
        DEACTIVATION_DATE: r.DEACTIVATION_DATE,
        SUBSCRIBERS_EMAIL_ADDRESS: r.SUBSCRIBERS_EMAIL_ADDRESS,
      })),
    };
    // IT-F2-426 b6683cc5: when the checkbox is on, embed the visible rows
    // for the active tab so the shared JSON tells the whole story — not
    // just filter state + first 5 sample.
    if (this.debug_copy_include_view_data) {
      const view_data: any = { view_mode: this.view_mode };
      if (this.view_mode === 'users' || this.view_mode === 'login_periods') {
        view_data.rows = filtered;
      } else if (this.view_mode === 'monthly_counts') {
        view_data.rows = this.monthly_counts_rows;
      } else if (this.view_mode === 'source_coverage') {
        view_data.rows = this.source_coverage_rows;
      } else if (this.view_mode === 'employees') {
        view_data.rows = this.employee_rows;
      } else if (this.view_mode === 'report_page') {
        view_data.source_events_rows  = this.source_events_rows;
        view_data.source_uniques_rows = this.source_uniques_rows;
      }
      snapshot.view_data = view_data;
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
      const view_bit = this.debug_copy_include_view_data ? ' + view data' : '';
      this.toast.success('Debug snapshot copied', `${filtered.length} filtered rows, ${selected.length} selected${view_bit}`);
    }
    catch (e: any) {
      this.toast.error('Copy failed', e?.message || 'clipboard denied');
    }
  }

  clear_selection() {
    if (this.grid?.api) {
      this.grid.api.deselectAll();
      this.selected_count = 0;
    }
  }

  // IT-F2-223 Mike 2026-09-01: click Pro/Non-Pro cell → flip via backend.
  // Optimistic-update the row locally so the color changes immediately;
  // if the server rejects we roll back + refresh from source.
  async on_pro_status_click(evt: any) {
    const row = evt?.data;
    if (!row) return;
    // USER_ID column now displays the F2 UUID (Cognito sub) per Mike
    // 2026-09-01 c/c3af6599. Backend agreements are still keyed by the
    // original user_id (email-shaped), preserved in USER_ID_INTERNAL.
    const user_id = row.USER_ID_INTERNAL || row.USER_ID;
    const customer_slug = this._resolve_customer_slug(row);
    if (!user_id || !customer_slug) {
      this.toast.error('Cannot flip — missing user_id or customer', '');
      return;
    }
    const current_is_pro = row.PRO_STATUS === 'Pro';
    const new_is_pro = !current_is_pro;
    // Optimistic local flip
    row.PRO_STATUS = new_is_pro ? 'Pro' : 'Non-Pro';
    evt.api.applyTransaction({ update: [row] });

    const res: any = await this.adminService.setAgreementProAccess(user_id, customer_slug, new_is_pro);
    if (res?.err || !res?.success) {
      // Rollback
      row.PRO_STATUS = current_is_pro ? 'Pro' : 'Non-Pro';
      evt.api.applyTransaction({ update: [row] });
      this.toast.error('Flip failed', res?.err || 'server did not confirm');
      return;
    }
    this.toast.success('Updated', `${user_id} → ${new_is_pro ? 'Pro' : 'Non-Pro'} (${res.source})`);
    // Backend already invalidated cache — no need to reload the whole
    // grid. Local optimistic value is now authoritative until next refresh.
  }

  private _resolve_customer_slug(row: any): string {
    // Backend row includes CUSTOMER as a display-only column — always
    // present, unambiguous across multi-customer views.
    if (row?.CUSTOMER) return row.CUSTOMER;
    // Legacy fallback if a stale cached row is missing it: use filter
    // selection when exactly one customer is picked.
    const filt = this.filter_customers.value || [];
    if (filt.length === 1) return filt[0];
    return '';
  }

  init_grid() {
    // Grid columns: 36 Exhibit B + display-only extras. All visible by
    // default so operator sees exactly what Copy-to-Excel contains. CSV
    // export narrows back to the 36 spec columns.
    const columnDefs: any[] = [
      // Leading checkbox column — selecting rows narrows all exports
      // (Pro / Non-Pro / Copy Excel / all-CSV / monthly counts) to
      // only the selected users. Mike ask 2026-09-01 c/a0b9b549.
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 44,
        maxWidth: 44,
        pinned: 'left',
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
      },
      ...EXHIBIT_B_COLS.map(c => {
        // Q1-Q11 hold single-char Y/N — narrow + centered. Other cols
        // stay wide so long fields (addresses, employer names) aren't
        // truncated. Mike ask 2026-09-01 to right-size the Q columns.
        const is_q = /^Q\d+$/.test(c.key);
        return {
          field: c.key,
          headerName: c.header,
          minWidth: is_q ? 55 : 130,
          maxWidth: is_q ? 70 : undefined,
          resizable: true,
          filter: is_q ? 'agSetColumnFilter' : 'agTextColumnFilter',
          cellStyle: is_q
            ? { 'text-align': 'center', 'font-weight': '600' }
            : { 'text-align': 'left' },
        };
      }),
      // Customer slug pinned RIGHT (quick reference for every row).
      // Pro/Non-Pro toggle also pinned. Other display cols scroll with the
      // main grid to avoid the split-horizontal-scroll problem when too many
      // columns are pinned. Mike ask 2026-09-01.
      {
        field: 'CUSTOMER',
        headerName: 'Customer',
        minWidth: 130,
        resizable: true,
        filter: 'agSetColumnFilter',
        cellStyle: { 'text-align': 'left', 'font-weight': '500' },
      },
      // Scanners the user has (or had) under this customer, comma-delimited.
      // Matches Block A col 9 in the reference MTA compliance file.
      // Mike course-correction 2026-09-01: one row per user+customer with
      // scanners as a list, not per-scanner rows.
      {
        field: 'SCANNERS',
        headerName: 'Scanners',
        minWidth: 260,
        resizable: true,
        filter: 'agTextColumnFilter',
        cellStyle: { 'text-align': 'left', 'font-size': '11px' },
        tooltipField: 'SCANNERS',
      },
      // Pro/Non-Pro toggle — clickable, flips via backend endpoint.
      {
        field: 'PRO_STATUS',
        headerName: 'Pro / Non-Pro',
        minWidth: 120,
        resizable: true,
        filter: 'agSetColumnFilter',
        cellStyle: (params: any) => ({
          'text-align': 'center',
          'font-weight': '600',
          cursor: 'pointer',
          color: params.value === 'Pro' ? '#fca5a5' : '#86efac',
        }),
        cellRenderer: (params: any) => {
          const v = params.value || '';
          const title = `Click to flip to ${v === 'Pro' ? 'Non-Pro' : 'Pro'}`;
          return `<span title="${title}" style="text-decoration:underline dotted">${v}</span>`;
        },
        onCellClicked: (evt: any) => this.on_pro_status_click(evt),
      },
      // EARLIEST_DATE — MIN(created_at) across main+legacy merge.
      {
        field: 'EARLIEST_DATE',
        headerName: 'First Signed',
        minWidth: 180,
        resizable: true,
        filter: 'agDateColumnFilter',
        valueFormatter: (p: any) => {
          if (!p.value) return '';
          // Parse as UTC — my "midnight detection" was using LOCAL hour,
          // which for a user in EST turned "2025-12-01T00:00:00Z" (Dec 1
          // UTC) into local hour=19 → not-midnight → "11/30/2025 19:00:00"
          // — off by a day (Mike c/5e8d1cfb). Also treat UTC end-of-day
          // (23:59:59) as a date anchor since backend emits period
          // deactivation as "last day of month at 23:59:59.999Z" — that
          // was shipping as "06/30/2025 16:59:59" PDT (Mike c/8a5dafa3).
          const utc = DateTime.fromISO(p.value, { zone: 'utc' });
          if (!utc.isValid) return '';
          const is_date_anchor =
            (utc.hour === 0 && utc.minute === 0 && utc.second === 0) ||
            (utc.hour === 23 && utc.minute === 59 && utc.second === 59);
          return is_date_anchor
            ? utc.toFormat('MM/dd/yyyy')
            : utc.toLocal().toFormat('MM/dd/yyyy HH:mm:ss');
        },
      },
      // DEACTIVATION_DATE — from UserTracking end_date; blank when active.
      // Red-tinted so deactivated users stand out from the active majority.
      {
        field: 'DEACTIVATION_DATE',
        headerName: 'Deactivated',
        minWidth: 180,
        resizable: true,
        filter: 'agDateColumnFilter',
        cellStyle: (params: any) => ({
          'text-align': 'center',
          color: params.value ? '#fca5a5' : undefined,
        }),
        valueFormatter: (p: any) => {
          if (!p.value) return '';
          // Parse as UTC — my "midnight detection" was using LOCAL hour,
          // which for a user in EST turned "2025-12-01T00:00:00Z" (Dec 1
          // UTC) into local hour=19 → not-midnight → "11/30/2025 19:00:00"
          // — off by a day (Mike c/5e8d1cfb). Also treat UTC end-of-day
          // (23:59:59) as a date anchor since backend emits period
          // deactivation as "last day of month at 23:59:59.999Z" — that
          // was shipping as "06/30/2025 16:59:59" PDT (Mike c/8a5dafa3).
          const utc = DateTime.fromISO(p.value, { zone: 'utc' });
          if (!utc.isValid) return '';
          const is_date_anchor =
            (utc.hour === 0 && utc.minute === 0 && utc.second === 0) ||
            (utc.hour === 23 && utc.minute === 59 && utc.second === 59);
          return is_date_anchor
            ? utc.toFormat('MM/dd/yyyy')
            : utc.toLocal().toFormat('MM/dd/yyyy HH:mm:ss');
        },
      },
      // Emails — real email, distinct from USER_ID (which is now the
      // F2 UUID) per Mike 2026-09-01 c/c3af6599.
      {
        field: 'SUBSCRIBERS_EMAIL_ADDRESS',
        headerName: 'Emails',
        minWidth: 220,
        resizable: true,
        filter: 'agTextColumnFilter',
        cellStyle: { 'text-align': 'left', 'font-family': 'monospace', 'font-size': '11px' },
      },
      // Last Login — troubleshooting. Mike ask 2026-09-01 c/3fb76608.
      {
        field: 'LAST_LOGIN',
        headerName: 'Last Login',
        minWidth: 180,
        resizable: true,
        filter: 'agDateColumnFilter',
        valueFormatter: (p: any) => {
          if (!p.value) return '';
          const utc = DateTime.fromISO(p.value, { zone: 'utc' });
          if (!utc.isValid) return '';
          return utc.toLocal().toFormat('MM/dd/yyyy HH:mm:ss');
        },
      },
    ];

    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        // IT-F2-426 (Mike checklist 7d05d863): renders headerTooltip +
        // tooltipField values as native browser title attributes so the
        // OS shows a hover hint. Ag-grid's default (custom overlay) only
        // fires when a tooltipComponent is registered — without one, the
        // headerTooltip strings I set on the columns silently disappeared.
        // Native title also works on touch-devices' long-press.
        enableBrowserTooltips: true,
        // IT-F2-426 (Mike checklist 1898d25d): "all columns need a copy
        // hint option on their right click menu". Injects a "Copy hint"
        // item at the top of every column's right-click menu — copies
        // that column's headerTooltip (or headerName as a fallback when
        // the column has no tooltip) to the clipboard. The ag-grid
        // defaults ("Copy", "Copy with Headers", "Paste", "Export",
        // "Auto-size", etc.) follow after a separator.
        getContextMenuItems: (params: any) => {
          const col = params?.column;
          const colDef: any = col?.getColDef?.() || {};
          const hint: string =
            String(colDef.headerTooltip ||
                   colDef.tooltipField ||
                   colDef.headerName ||
                   colDef.field || '').trim();
          const copyHintItem = {
            name: 'Copy hint',
            icon: '<i class="fa-solid fa-circle-info"></i>',
            action: () => {
              try {
                if (navigator?.clipboard?.writeText) {
                  navigator.clipboard.writeText(hint);
                } else {
                  // Fallback for insecure contexts — off-DOM textarea
                  // + execCommand('copy').
                  const ta = document.createElement('textarea');
                  ta.value = hint;
                  ta.style.position = 'fixed';
                  ta.style.opacity = '0';
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand('copy');
                  document.body.removeChild(ta);
                }
                this.toast?.success?.('Copied', hint.slice(0, 120) + (hint.length > 120 ? '…' : ''));
              } catch (e: any) {
                this.toast?.error?.('Copy failed', String(e?.message || e));
              }
            },
            disabled: !hint,
          };
          const defaults = params?.defaultItems || [];
          return [copyHintItem, 'separator', ...defaults];
        },
        defaultColDef: {
          floatingFilter: true,
          filter: true,
        },
        // Row id must be unique across all view modes. Monthly rows have
        // MONTH/COUNT/CUMULATIVE (no USER_ID) — falling back to a plain
        // USER_ID template would produce "undefined|undefined" for every
        // monthly row and ag-grid would dedupe all 29 months to 1
        // (Mike caught 2026-09-01 IT-F2-223). Branch on the fields
        // actually present in the row.
        getRowId: (params: any) => {
          if (params.data?.MONTH !== undefined) return `m|${params.data.MONTH}`;
          // Include EARLIEST_DATE so login-periods mode (one row per usage
          // period) doesn't collide — same user can appear multiple times
          // with different activation dates.
          return `u|${params.data?.USER_ID}|${params.data?.EARLIEST_DATE || params.data?.DATE_AND_TIME_STAMP}`;
        },
        onFilterChanged: () => {
          this._sync_url_from_filters();
          this._rebuild_monthly_counts();
        },
        onRowClicked: (evt: any) => this.on_monthly_row_click(evt),
        rowSelection: 'multiple',
        suppressRowClickSelection: true,  // click checkbox to select, not the row body
        onSelectionChanged: (evt: any) => {
          this.selected_count = evt.api?.getSelectedNodes?.().length || 0;
        },
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
        ]
      },
      columnDefs
    };
  }

  on_grid_ready(e: any) {
    this.grid.api = e.api;
    this.grid.colApi = e.colApi;
    // IT-F2-426 b209e2d5 (Mike 2026-09-25 c/1c276b06): grid mounts with the
    // default (Users) columnDefs from grid.columnDefs, so a cold refresh
    // on ?tab=monthly_counts (or any non-Users tab) lands on a blank grid
    // — right rowData bound via [rowData], but the columns are still USERS.
    // Reconcile now that the grid API is available.
    if (this.view_mode !== 'users' && this.view_mode !== 'employees' && this.view_mode !== 'report_page') {
      const defs = this._current_column_defs();
      if (defs && defs.length) e.api.setGridOption('columnDefs', defs);
      const rowData =
        this.view_mode === 'monthly_counts'  ? this.monthly_counts_rows :
        this.view_mode === 'source_coverage' ? this.source_coverage_rows :
        this.rows;
      e.api.setGridOption('rowData', rowData);
    }
    // Apply any grid-filter model captured from the URL at ngOnInit time
    // (see _initial_gf_from_url — route.snapshot is a live getter and
    // our pre-grid-ready _sync_url_from_filters() call will have wiped
    // the param before we could read it here).
    const gf_raw = this._initial_gf_from_url;
    if (gf_raw) {
      try {
        const model = JSON.parse(decodeURIComponent(gf_raw));
        if (model && typeof model === 'object') e.api.setFilterModel(model);
      } catch { /* ignore malformed gf param */ }
    }
  }
}
